import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Typography,
  Grid,
} from "@mui/material";
import { useSelector } from "react-redux";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AttachmentIcon from "@mui/icons-material/Attachment";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { RootState } from "../../../store/store";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../hooks/useAppDispatch";
import CustomButton from "../../../components/customButton/customButton";
import CustomTabs from "../../../components/customTabs/customTabs";
import { GeneralStyles } from "../../../assets/css/general.styles";
import CustomDatePicker from "../../../components/customDatePicker/customDatePicker";
import CustomTextField from "../../../components/customTextField/customTextField";
import {
  SAVE_OPPORTUNITY,
  VALIDATION_ACTIVATION_DATE_MESSAGE,
  VALIDATION_ATTACHED_FILES,
  VALIDATION_ATTACHED_FILES_TYPES,
  VALIDATION_ATTACHED_IMAGE_TYPES,
  VALIDATION_ATTACHED_VIDEO_TYPES,
  VALIDATION_CMA_START_DATE_MESSAGE,
  VALIDATION_CMA_SUBMISSION_DATE_MESSAGE,
  VALIDATION_DATE_RANGE_MESSAGE,
  VALIDATION_DETAILS_DATE_MESSAGE,
  VALIDATION_DETAILS_FINALIZING_DATE_MESSAGE,
  VALIDATION_ERROR_ACTIVATING_DATE_REQUIRED_MESSAGE,
  VALIDATION_ERROR_ALLOTMENT_DATE_MESSAGE,
  VALIDATION_ERROR_BETWEEN_RANGE_MESSAGE,
  VALIDATION_ERROR_DATE_MESSAGE,
  VALIDATION_ERROR_DEADLINE_DATE_MESSAGE,
  VALIDATION_ERROR_EARLIER_DATE_REQUIRED_MESSAGE,
  VALIDATION_ERROR_FIELDS_REQUIRED_MESSAGE,
  VALIDATION_ERROR_INPUT_NUMBER_INRANGE_MESSAGE,
  VALIDATION_ERROR_INPUT_NUMBER_MESSAGE,
  VALIDATION_ERROR_MAXIMUM_TARGET_NUMBER_MESSAGE,
  VALIDATION_ERROR_RANGE_AMOUNT_MESSAGE,
  VALIDATION_ERROR_REQUIRED_INPUT_RANGE,
  VALIDATION_ERROR_REQUIRED_MESSAGE,
  VALIDATION_ERROR_REQUIRED_WITH_CORRECT_FORMAT_MESSAGE,
  VALIDATION_ERROR_TARGET_BETWEEN_RANGE_MESSAGE,
  VALIDATION_ERROR_TITLE_UNIQUE_MESSAGE,
  VALIDATION_FILES,
  VALIDATION_IMAGE_FILES,
} from "../../../constants/actions";
import { OpportunityTouched } from "./opportunityTouched";
import CustomSelect from "../../../components/customSelect/customSelect";
import { OpportunitiesResource } from "../../../models/resource/opportunitiesResource";
import CustomTextArea from "../../../components/customTextArea/customTextArea";
import CustomIcon from "../../../components/customIcon/customIcon";
import CustomPaginatedDataTable from "../../../components/customPaginatedDataTable/customPaginatedDataTable";
import { CustomTableColumnsProps } from "../../../components/customDataTable/customTableColumnsProps";
import { FileResource } from "../../../models/resource/fileResource";
import CustomFileInput from "../../../components/customFileInput/customFileInput";
import {
  allowedFileTypes,
  allowedImageExtensions,
  allowedVideoExtensions,
  fileTypes,
} from "../../../constants/fileTypes";
import CustomDialog from "../../../components/customDialog/customDialog";
import {
  maximumDinarValue,
  maximumTargetAmount,
  minimumDinarValue,
  minimumNumberOfShares,
} from "../../../constants/validations";
import { toast } from "react-toastify";
import {
  getCategories,
  getCompanyShariaaCompilant,
  getFundsPreviouslyOfferIssuer,
  getInvestorsNumberRestrictionList,
  getIslamicShariaComplianceList,
  getMainPurposeOfFundUtilization,
  getOfferIssuerRaiseFunds,
  getOwnershipstructureList,
  getPercentageFundsRaiseBefore,
  getSectors,
  getStatus,
  getTermsOfAgreementChangesList,
} from "../../../reducers/lookupReducer";
import {
  FileTypes,
  LookupEnum,
  OfferIssuerValues,
  SectorValues,
  ShariaCompliantValues,
  StatusValues,
} from "../../../constants/enums";
import { getOfferIssuer } from "../../../reducers/offerIssuerReducer";
import {
  clearBlobData,
  exportOpportunity,
  getFile,
  getOpportunityById,
  saveOpportunity,
} from "../../../reducers/opportunitiesReducer";
import { OpportunitiesModel } from "../../../models/models/opportunitiesModel";
import { OpportunityAdditionalInfoResource } from "../../../models/resource/opportunityAdditionalInfoResource";
import GridStyles from "../../../assets/css/grid.styles";
import { OpportunityOfferIssuerInfoResource } from "../../../models/resource/opportunityOfferIssuerInfoResource";
import Loader from "../../../components/loader";
import {
  isDateLaterThanToday,
  parseDate,
} from "../../../helpers/useDateHelpers";
import { formatNumberWithCommas } from "../../../helpers/useAmountHelpers";
import GetAppIcon from "@mui/icons-material/GetApp";
import theme from "../../../themes/index.en";
import {
  MAX_VIDEO_SIZE_BYTES,
  MAX_VIDEO_SIZE_MB,
} from "../../../constants/extensions";

const OpportunityDetailsForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const authToken = useSelector((state: RootState) => state.auth.token);
  const mainPurposeOfFundUtilizationList = useSelector(
    (state: RootState) => state.lookup.mainPurposeOfFundUtilizationList
  );
  const ownershipstructureList = useSelector(
    (state: RootState) => state.lookup.ownershipstructureList
  );
  const termsOfAgreementChangesList = useSelector(
    (state: RootState) => state.lookup.termsOfAgreementChangesList
  );
  const investorsNumberRestrictionList = useSelector(
    (state: RootState) => state.lookup.investorsNumberRestrictionList
  );
  const islamicShariaComplianceList = useSelector(
    (state: RootState) => state.lookup.islamicShariaComplianceList
  );
  const companyShariaaCompilantList = useSelector(
    (state: RootState) => state.lookup.companyShariaaCompilantList
  );
  const statusList = useSelector((state: RootState) => state.lookup.statusList);
  const sectorsList = useSelector(
    (state: RootState) => state.lookup.sectorsList
  );
  const categoriesList = useSelector(
    (state: RootState) => state.lookup.categoriesList
  );
  const offerIssuers = useSelector(
    (state: RootState) => state.offerIssuer.offerIssuerDetails
  );
  const opportunityDetails = useSelector(
    (state: RootState) => state.opportunities.opportunityDetails
  );
  const file = useSelector((state: RootState) => state.opportunities.file);
  const opportunity = useSelector(
    (state: RootState) => state.opportunities.opportunity
  );
  const [selectedTab, setSelectedTab] = useState(1);
  const [touched, setTouched] = useState<OpportunityTouched>({});
  const [deleteFileConfirmationOpen, setDeleteFileConfirmationOpen] =
    useState(false);
  const [selectedFileRow, setSelectedFileRow] = useState<FileResource | null>(
    null
  );
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [warningOfferIssuer, setWarningOfferIssuer] = useState(false);
  const [pendingFile, setPendingFile] = useState<any>(null);
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const [fileData, setFileData] = useState<FileResource>({
    typeId: 0,
    type: "",
    data: "",
    fileName: "",
    fileUrl: "",
    extension: "",
    contentType: "",
    size: 0,
    date: "",
  });
  const [additionalInfo, setAdditionalInfo] =
    useState<OpportunityAdditionalInfoResource>({
      id: 0,
      isOurEmployeesInvolved: false,
      isDirectorsInvolved: false,
      isOurShareholdersInvolved: false,
      isShariaCompliantId: null,
      shariaOpinionStatementEn: "",
      shariaOpinionStatementAr: "",
      sectorId: null,
      summaryEn: "",
      ourStoryEn: "",
      aboutTheCompanyEn: "",
      riskEn: "",
      howFundsUtilizedEn: "",
      competitiveAdvantageEn: "",
      feesAndChargesEn: "",
      disclaimerEn: "",
      summaryAr: "",
      ourStoryAr: "",
      aboutTheCompanyAr: "",
      riskAr: "",
      howFundsUtilizedAr: "",
      competitiveAdvantageAr: "",
      feesAndChargesAr: "",
      disclaimerAr: "",
      othersEn: "",
      othersAr: "",
    });
  const [offerIssuerInfo, setOfferIssuerInfo] =
    useState<OpportunityOfferIssuerInfoResource>({
      id: 0,
      securitiesNumber: 0,
      applicationDate: "",
      activationDate: "",
      investorsNumberRestrictionId: null,
      islamicShariaComplianceId: null,
      isAuditorReportsProvided: false,
      auditorReportsDocument: null,
      incomeStatementDocument: null,
      financialNotesStatementDocument: null,
      realEstateEvaluationDocument: null,
      cashFlowStatementDocument: null,
      equityChangesStatementDocument: null,
      isFinPositionStatementProvided: false,
      finPositionStatementDocument: null,
      isIncomeStatementProvided: false,
      isEquityChangesStatementProvided: false,
      isCashFlowStatementProvided: false,
      isFinancialNotesStatementProvided: false,
      isRealEstateEvaluationProvided: false,
      projectionsApplicable: "",
      riskDetails: "",
      financialProjectionAssumptions: "",
      shariaOpinion: "",
      isAlmouwaziConfirmed: false,
      isAicConfirmed: false,
      isCmaConfirmed: false,
      detailsFinalizingDate: "",
      almouwaziConfirmationDate: "",
      cmaSubmissionDate: "",
      almouwaziConfirmationComment: "",
      detailsFinalizingComment: "",
      cmaSubmissionComment: "",
    });

  const [formData, setFormData] = useState<OpportunitiesResource>({
    id: 0,
    titleAr: "",
    titleEn: "",
    offerIssuerId: null,
    statusId: StatusValues.Draft,
    fundraisingPurposeEn: "",
    fundraisingPurposeAr: "",
    startDate: "",
    endDate: "",
    collectFundsDeadline: "",
    securitiesAllotmentDate: "",
    eligibleOfferSharesNumber: null,
    sharesPercentage: "",
    subscriptionMinAmount: 0,
    subscriptionMaxAmount: 0,
    targetAmount: 0,
    issuerHistory: "",
    cancelReason: "",
    estimatedGrossNetProceeds: 0,
    internalRateReturn: 0,
    isInternalRateReturnVisible: false,
    investedAmount: 0,
    investorsCount: 0,
    ownershipStructureId: null,
    acquisitionPropertyAssetExplanation: "",
    acquisitionPropertyAssetAmount: 0,
    capitalExpenditureExplanation: "",
    capitalExpenditureAmount: 0,
    generalDayCapitalExplanation: "",
    generalDayCapitalAmount: 0,
    offerExpensesExplanation: "",
    offerExpensesAmount: 0,
    commissionBrokerageFeesExplanation: "",
    commissionBrokerageFeesAmount: 0,
    otherExplanation: "",
    otherAmount: 0,
    termsOfAgreementChangesId: null,
    changesDetails: "",
    additionalInfo,
    offerIssuerInfo,
    categories: [],
    fundUtilizationMainPurposes: [],
    files: [],
  });

  useEffect(() => {
    if (!authToken) {
      navigate("/Signin");
    }
  }, [authToken]);

  useEffect(() => {
    if (id) {
      const itemId = parseInt(id, 10);
      dispatch(getOpportunityById({ id: itemId }));
    }
  }, [id]);

  useEffect(() => {
    const itemData = opportunityDetails.data;

    if (itemData && id) {
      const data = itemData as OpportunitiesModel;
      const additionalInfoData =
        data.additionalInfo as OpportunityAdditionalInfoResource;
      const offerIssuerData =
        data.offerIssuerInfo as OpportunityOfferIssuerInfoResource;
      setFormData({
        id: data.id || 0,
        titleAr: data.titleAr || "",
        titleEn: data.titleEn || "",
        offerIssuerId: data.offerIssuerId || null,
        statusId: data.statusId || StatusValues.Draft,
        fundraisingPurposeEn: data.fundraisingPurposeEn || "",
        fundraisingPurposeAr: data.fundraisingPurposeAr || "",
        startDate: data.startDate || "",
        endDate: data.endDate || "",
        collectFundsDeadline: data.collectFundsDeadline || "",
        securitiesAllotmentDate: data.securitiesAllotmentDate || "",
        eligibleOfferSharesNumber: data.eligibleOfferSharesNumber || null,
        sharesPercentage: data.sharesPercentage || "",
        subscriptionMinAmount: data.subscriptionMinAmount || 0,
        subscriptionMaxAmount: data.subscriptionMaxAmount || 0,
        targetAmount: data.targetAmount || 0,
        issuerHistory: data.issuerHistory || "",
        estimatedGrossNetProceeds: data.estimatedGrossNetProceeds || 0,
        internalRateReturn: data.internalRateReturn || 0,
        isInternalRateReturnVisible: data.isInternalRateReturnVisible || false,
        investedAmount: data.investedAmount || 0,
        investorsCount: data.investorsCount || 0,
        ownershipStructureId: data.ownershipStructureId || null,
        acquisitionPropertyAssetExplanation:
          data.acquisitionPropertyAssetExplanation || "",
        acquisitionPropertyAssetAmount:
          data.acquisitionPropertyAssetAmount || 0,
        capitalExpenditureExplanation: data.capitalExpenditureExplanation || "",
        capitalExpenditureAmount: data.capitalExpenditureAmount || 0,
        generalDayCapitalExplanation: data.generalDayCapitalExplanation || "",
        generalDayCapitalAmount: data.generalDayCapitalAmount || 0,
        offerExpensesExplanation: data.offerExpensesExplanation || "",
        offerExpensesAmount: data.offerExpensesAmount || 0,
        commissionBrokerageFeesExplanation:
          data.commissionBrokerageFeesExplanation || "",
        commissionBrokerageFeesAmount: data.commissionBrokerageFeesAmount || 0,
        otherExplanation: data.otherExplanation || "",
        otherAmount: data.otherAmount || 0,
        termsOfAgreementChangesId: data.termsOfAgreementChangesId || null,
        changesDetails: data.changesDetails || "",
        cancelReason: data.cancelReason || "",
        additionalInfo: data.additionalInfo || additionalInfo,
        offerIssuerInfo: data.offerIssuerInfo || offerIssuerInfo,
        categories: data.categories.map((item) => item.categoryId) || [],
        fundUtilizationMainPurposes:
          data.fundUtilizationMainPurposes.map((item) => item.purposeId) || [],
        files: data.files || [],
      });
      if (data.coverImage && data.coverImage.typeId !== 0) {
        setFormData((prevData) => ({
          ...prevData,
          files: [...(prevData.files || []), data.coverImage],
        }));
      }
      setAdditionalInfo({
        id: additionalInfoData.id || 0,
        isOurEmployeesInvolved:
          additionalInfoData.isOurEmployeesInvolved || false,
        isDirectorsInvolved: additionalInfoData.isDirectorsInvolved || false,
        isOurShareholdersInvolved:
          additionalInfoData.isOurShareholdersInvolved || false,
        isShariaCompliantId: additionalInfoData.isShariaCompliantId || null,
        shariaOpinionStatementEn:
          additionalInfoData.shariaOpinionStatementEn || "",
        shariaOpinionStatementAr:
          additionalInfoData.shariaOpinionStatementAr || "",
        sectorId: additionalInfoData.sectorId || null,
        summaryEn: additionalInfoData.summaryEn || "",
        ourStoryEn: additionalInfoData.ourStoryEn || "",
        aboutTheCompanyEn: additionalInfoData.aboutTheCompanyEn || "",
        riskEn: additionalInfoData.riskEn || "",
        howFundsUtilizedEn: additionalInfoData.howFundsUtilizedEn || "",
        competitiveAdvantageEn: additionalInfoData.competitiveAdvantageEn || "",
        feesAndChargesEn: additionalInfoData.feesAndChargesEn || "",
        disclaimerEn: additionalInfoData.disclaimerEn || "",
        summaryAr: additionalInfoData.summaryAr || "",
        ourStoryAr: additionalInfoData.ourStoryAr || "",
        aboutTheCompanyAr: additionalInfoData.aboutTheCompanyAr || "",
        riskAr: additionalInfoData.riskAr || "",
        howFundsUtilizedAr: additionalInfoData.howFundsUtilizedAr || "",
        competitiveAdvantageAr: additionalInfoData.competitiveAdvantageAr || "",
        feesAndChargesAr: additionalInfoData.feesAndChargesAr || "",
        disclaimerAr: additionalInfoData.disclaimerAr || "",
        othersEn: additionalInfoData.othersEn || "",
        othersAr: additionalInfoData.othersAr || "",
      });

      setOfferIssuerInfo({
        id: offerIssuerData.id || 0,
        securitiesNumber: offerIssuerData.securitiesNumber || 0,
        applicationDate: offerIssuerData.applicationDate || "",
        activationDate: offerIssuerData.activationDate || "",
        investorsNumberRestrictionId:
          offerIssuerData.investorsNumberRestrictionId || null,
        islamicShariaComplianceId:
          offerIssuerData.islamicShariaComplianceId || null,
        auditorReportsDocument: offerIssuerData.auditorReportsDocument || null,
        incomeStatementDocument:
          offerIssuerData.incomeStatementDocument || null,
        financialNotesStatementDocument: offerIssuerData.financialNotesStatementDocument || null,
        realEstateEvaluationDocument:   offerIssuerData.realEstateEvaluationDocument || null,
        cashFlowStatementDocument:
          offerIssuerData.cashFlowStatementDocument || null,
        equityChangesStatementDocument:
          offerIssuerData.equityChangesStatementDocument || null,
        finPositionStatementDocument:
          offerIssuerData.finPositionStatementDocument || null,
        isAuditorReportsProvided:
          offerIssuerData.isAuditorReportsProvided || false,
        isFinPositionStatementProvided:
          offerIssuerData.isFinPositionStatementProvided || false,
        isIncomeStatementProvided:
          offerIssuerData.isIncomeStatementProvided || false,
        isEquityChangesStatementProvided:
          offerIssuerData.isEquityChangesStatementProvided || false,
        isCashFlowStatementProvided:
          offerIssuerData.isCashFlowStatementProvided || false,
        isFinancialNotesStatementProvided:  offerIssuerData.isFinancialNotesStatementProvided || false,
        isRealEstateEvaluationProvided:  offerIssuerData.isRealEstateEvaluationProvided || false,
        projectionsApplicable: offerIssuerData.projectionsApplicable || "",
        riskDetails: offerIssuerData.riskDetails || "",
        financialProjectionAssumptions:
          offerIssuerData.financialProjectionAssumptions || "",
        shariaOpinion: offerIssuerData.shariaOpinion || "",
        isAlmouwaziConfirmed: offerIssuerData.isAlmouwaziConfirmed || false,
        isAicConfirmed: offerIssuerData.isAicConfirmed || false,
        isCmaConfirmed: offerIssuerData.isCmaConfirmed || false,
        almouwaziConfirmationDate: offerIssuerData.almouwaziConfirmationDate || "",
        almouwaziConfirmationComment: offerIssuerData.almouwaziConfirmationComment || "",
        detailsFinalizingComment: offerIssuerData.detailsFinalizingComment || "",
        cmaSubmissionComment: offerIssuerData.cmaSubmissionComment || "",
        detailsFinalizingDate: offerIssuerData.detailsFinalizingDate || "",
        cmaSubmissionDate: offerIssuerData.cmaSubmissionDate || "",
      });
    }
  }, [opportunityDetails.data]);

  useEffect(() => {
    dispatch(
      getMainPurposeOfFundUtilization({
        lookupId: LookupEnum.MainPurposeOfFundUtilization,
      })
    );
    dispatch(
      getTermsOfAgreementChangesList({
        lookupId: LookupEnum.TermsOfAgreementChangesList,
      })
    );
    dispatch(
      getInvestorsNumberRestrictionList({
        lookupId: LookupEnum.InvestorsNumberRestriction,
      })
    );
    dispatch(
      getIslamicShariaComplianceList({
        lookupId: LookupEnum.IslamicShariaCompliance,
      })
    );
    dispatch(
      getOwnershipstructureList({ lookupId: LookupEnum.Ownershipstructure })
    );
    dispatch(
      getOfferIssuerRaiseFunds({ lookupId: LookupEnum.OfferIssuerRaiseFunds })
    );
    dispatch(
      getPercentageFundsRaiseBefore({
        lookupId: LookupEnum.PercentageFundsRaiseBefore,
      })
    );
    dispatch(
      getFundsPreviouslyOfferIssuer({
        lookupId: LookupEnum.FundsPreviouslyOfferIssuer,
      })
    );
    dispatch(
      getCompanyShariaaCompilant({
        lookupId: LookupEnum.CompanyShariaaCompilant,
      })
    );
    dispatch(getSectors({ lookupId: LookupEnum.Sectors }));
    dispatch(getCategories({ lookupId: LookupEnum.OpportunityCategories }));
    dispatch(getStatus({ lookupId: LookupEnum.Status }));
    dispatch(getOfferIssuer());
  }, []);

  const handleSave = () => {
    if (
      formData.fundraisingPurposeEn.trim() === "" ||
      formData.fundraisingPurposeAr.trim() === "" ||
      formData.titleAr.trim() === "" ||
      formData.titleEn.trim() === "" ||
      formData.titleAr === formData.titleEn ||
      formData.issuerHistory.trim() === "" ||
      formData.ownershipStructureId === null ||
      (offerIssuerInfo.isCmaConfirmed && formData.startDate.trim() === "") ||
      (offerIssuerInfo.isCmaConfirmed && formData.endDate.trim() === "") ||
      (offerIssuerInfo.isCmaConfirmed &&
        formData.collectFundsDeadline.trim() === "") ||
      isDateLaterThanToday(offerIssuerInfo.applicationDate) ||
      offerIssuerInfo.applicationDate.trim() === "" ||
      (offerIssuerInfo.isCmaConfirmed &&
        offerIssuerInfo.activationDate.trim() === "") ||
      formData.acquisitionPropertyAssetExplanation.trim() === "" ||
      formData.capitalExpenditureExplanation.trim() === "" ||
      formData.generalDayCapitalExplanation.trim() === "" ||
      formData.offerExpensesExplanation.trim() === "" ||
      formData.commissionBrokerageFeesExplanation.trim() === "" ||
      formData.otherExplanation.trim() === "" ||
      additionalInfo.summaryAr.trim() === "" ||
      additionalInfo.summaryEn.trim() === "" ||
      additionalInfo.ourStoryAr.trim() === "" ||
      additionalInfo.ourStoryEn.trim() === "" ||
      additionalInfo.aboutTheCompanyAr.trim() === "" ||
      additionalInfo.aboutTheCompanyEn.trim() === "" ||
      additionalInfo.riskEn.trim() === "" ||
      additionalInfo.riskAr.trim() === "" ||
      additionalInfo.howFundsUtilizedAr.trim() === "" ||
      additionalInfo.howFundsUtilizedEn.trim() === "" ||
      formData.categories.length === 0 ||
      formData.offerIssuerId === null ||
      formData.statusId === null ||
      formData.termsOfAgreementChangesId === null ||
      additionalInfo.sectorId === null ||
      formData.fundUtilizationMainPurposes.length === 0 ||
      additionalInfo.isShariaCompliantId === null ||
      formData.subscriptionMinAmount === 0 ||
      formData.subscriptionMaxAmount === 0 ||
      formData.subscriptionMaxAmount > maximumTargetAmount ||
      formData.targetAmount === 0 ||
      formData.acquisitionPropertyAssetAmount === 0 ||
      formData.acquisitionPropertyAssetAmount >
        formData.subscriptionMaxAmount ||
      formData.capitalExpenditureAmount === 0 ||
      formData.capitalExpenditureAmount > formData.subscriptionMaxAmount ||
      formData.generalDayCapitalAmount === 0 ||
      formData.generalDayCapitalAmount > formData.subscriptionMaxAmount ||
      formData.offerExpensesAmount === 0 ||
      formData.offerExpensesAmount > formData.subscriptionMaxAmount ||
      formData.commissionBrokerageFeesAmount === 0 ||
      formData.commissionBrokerageFeesAmount > formData.subscriptionMaxAmount ||
      formData.otherAmount === 0 ||
      formData.investedAmount === 0 ||
      formData.internalRateReturn === 0 ||
      formData.investorsCount === 0 ||
      formData.otherAmount > formData.subscriptionMaxAmount ||
      (formData.investedAmount !== null &&
        isNaN(Number(formData.investedAmount))) ||
      (formData.internalRateReturn !== null &&
        isNaN(Number(formData.internalRateReturn))) ||
      (formData.investorsCount !== null &&
        isNaN(Number(formData.investorsCount))) ||
      (formData.subscriptionMinAmount !== null &&
        isNaN(Number(formData.subscriptionMinAmount))) ||
      (formData.subscriptionMaxAmount !== null &&
        isNaN(Number(formData.subscriptionMaxAmount))) ||
      (formData.targetAmount !== null &&
        isNaN(Number(formData.targetAmount))) ||
      formData.targetAmount > formData.subscriptionMaxAmount ||
      formData.targetAmount < formData.subscriptionMinAmount ||
      (formData.acquisitionPropertyAssetAmount !== null &&
        isNaN(Number(formData.acquisitionPropertyAssetAmount))) ||
      (formData.capitalExpenditureAmount !== null &&
        isNaN(Number(formData.capitalExpenditureAmount))) ||
      (formData.generalDayCapitalAmount !== null &&
        isNaN(Number(formData.generalDayCapitalAmount))) ||
      (formData.offerExpensesAmount !== null &&
        isNaN(Number(formData.offerExpensesAmount))) ||
      (formData.commissionBrokerageFeesAmount !== null &&
        isNaN(Number(formData.commissionBrokerageFeesAmount))) ||
      (formData.otherAmount !== null && isNaN(Number(formData.otherAmount))) ||
      (formData.eligibleOfferSharesNumber !== null &&
        (isNaN(Number(formData.eligibleOfferSharesNumber)) ||
          Number(formData.eligibleOfferSharesNumber) <
            minimumNumberOfShares)) ||
      isNaN(Number(formData.sharesPercentage)) ||
      Number(formData.sharesPercentage) < 0 ||
      Number(formData.sharesPercentage) > 100 ||
      formData.estimatedGrossNetProceeds === 0 ||
      (formData.estimatedGrossNetProceeds !== null &&
        isNaN(Number(formData.estimatedGrossNetProceeds))) ||
      (formData.estimatedGrossNetProceeds !== 0 &&
        (formData.estimatedGrossNetProceeds < minimumDinarValue ||
          formData.estimatedGrossNetProceeds > maximumDinarValue)) ||
      offerIssuerInfo.securitiesNumber === 0 ||
      (offerIssuerInfo.securitiesNumber !== null &&
        isNaN(Number(offerIssuerInfo.securitiesNumber))) ||
      offerIssuerInfo.investorsNumberRestrictionId === null ||
      offerIssuerInfo.islamicShariaComplianceId === null ||
      offerIssuerInfo.projectionsApplicable.trim() === "" ||
      offerIssuerInfo.riskDetails.trim() === "" ||
      additionalInfo.disclaimerEn.trim() === "" ||
      additionalInfo.disclaimerAr.trim() === "" ||
      additionalInfo.feesAndChargesEn.trim() === "" ||
      additionalInfo.feesAndChargesAr.trim() === "" ||
      additionalInfo.competitiveAdvantageEn.trim() === "" ||
      additionalInfo.competitiveAdvantageAr.trim() === "" ||
      offerIssuerInfo.financialProjectionAssumptions.trim() === "" ||
      (additionalInfo.isShariaCompliantId === ShariaCompliantValues.Yes &&
        additionalInfo.shariaOpinionStatementEn.trim() === "") ||
      (additionalInfo.isShariaCompliantId === ShariaCompliantValues.Yes &&
        additionalInfo.shariaOpinionStatementAr.trim() === "") ||
      (formData.statusId === StatusValues.Cancelled &&
        formData.cancelReason.trim() === "") ||
      (offerIssuerInfo.isAlmouwaziConfirmed &&
        offerIssuerInfo.almouwaziConfirmationDate.trim() === "") ||
      (offerIssuerInfo.isAicConfirmed &&
        offerIssuerInfo.detailsFinalizingDate.trim() === "") ||
      (offerIssuerInfo.isCmaConfirmed &&
        offerIssuerInfo.cmaSubmissionDate.trim() === "")
    ) {
      let touchedFields = {
        subscriptionMinAmount:
          formData.subscriptionMinAmount === 0 ||
          (formData.subscriptionMinAmount !== null &&
            isNaN(Number(formData.subscriptionMinAmount))),
        subscriptionMaxAmount:
          formData.subscriptionMaxAmount === 0 ||
          (formData.subscriptionMaxAmount !== null &&
            isNaN(Number(formData.subscriptionMaxAmount))) ||
          formData.subscriptionMaxAmount > maximumTargetAmount,
        targetAmount:
          formData.targetAmount === 0 ||
          (formData.targetAmount !== null &&
            isNaN(Number(formData.targetAmount))) ||
          formData.targetAmount > formData.subscriptionMaxAmount ||
          formData.targetAmount < formData.subscriptionMinAmount,
        acquisitionPropertyAssetAmount:
          formData.acquisitionPropertyAssetAmount === 0 ||
          (formData.acquisitionPropertyAssetAmount !== null &&
            isNaN(Number(formData.acquisitionPropertyAssetAmount))) ||
          formData.acquisitionPropertyAssetAmount >
            formData.subscriptionMaxAmount,
        capitalExpenditureAmount:
          formData.capitalExpenditureAmount === 0 ||
          (formData.capitalExpenditureAmount !== null &&
            isNaN(Number(formData.capitalExpenditureAmount))) ||
          formData.capitalExpenditureAmount > formData.subscriptionMaxAmount,
        generalDayCapitalAmount:
          formData.generalDayCapitalAmount === 0 ||
          (formData.generalDayCapitalAmount !== null &&
            isNaN(Number(formData.generalDayCapitalAmount))) ||
          formData.generalDayCapitalAmount > formData.subscriptionMaxAmount,
        offerExpensesAmount:
          formData.offerExpensesAmount === 0 ||
          (formData.offerExpensesAmount !== null &&
            isNaN(Number(formData.offerExpensesAmount))) ||
          formData.offerExpensesAmount > formData.subscriptionMaxAmount,
        commissionBrokerageFeesAmount:
          formData.commissionBrokerageFeesAmount === 0 ||
          (formData.commissionBrokerageFeesAmount !== null &&
            isNaN(Number(formData.commissionBrokerageFeesAmount))) ||
          formData.commissionBrokerageFeesAmount >
            formData.subscriptionMaxAmount,
        otherAmount:
          formData.otherAmount === 0 ||
          (formData.otherAmount !== null &&
            isNaN(Number(formData.otherAmount))) ||
          formData.otherAmount > formData.subscriptionMaxAmount,
        fundraisingPurposeEn: formData.fundraisingPurposeEn.trim() === "",
        fundraisingPurposeAr: formData.fundraisingPurposeAr.trim() === "",
        titleAr:
          formData.titleAr.trim() === "" ||
          formData.titleAr === formData.titleEn,
        titleEn:
          formData.titleEn.trim() === "" ||
          formData.titleAr === formData.titleEn,
        issuerHistory: formData.issuerHistory.trim() === "",
        ownershipStructureId: formData.ownershipStructureId === null,
        startDate:
          offerIssuerInfo.isCmaConfirmed && formData.startDate.trim() === "",
        endDate:
          offerIssuerInfo.isCmaConfirmed && formData.endDate.trim() === "",
        collectFundsDeadline:
          offerIssuerInfo.isCmaConfirmed &&
          formData.collectFundsDeadline.trim() === "",
        summaryAr: additionalInfo.summaryAr.trim() === "",
        summaryEn: additionalInfo.summaryEn.trim() === "",
        ourStoryEn: additionalInfo.ourStoryEn.trim() === "",
        ourStoryAr: additionalInfo.ourStoryAr.trim() === "",
        aboutTheCompanyEn: additionalInfo.aboutTheCompanyEn.trim() === "",
        aboutTheCompanyAr: additionalInfo.aboutTheCompanyAr.trim() === "",
        riskEn: additionalInfo.riskEn.trim() === "",
        riskAr: additionalInfo.riskAr.trim() === "",
        shariaOpinionStatementEn:
          additionalInfo.isShariaCompliantId === ShariaCompliantValues.Yes &&
          additionalInfo.shariaOpinionStatementEn.trim() === "",
        shariaOpinionStatementAr:
          additionalInfo.isShariaCompliantId === ShariaCompliantValues.Yes &&
          additionalInfo.shariaOpinionStatementAr.trim() === "",
        howFundsUtilizedEn: additionalInfo.howFundsUtilizedEn.trim() === "",
        howFundsUtilizedAr: additionalInfo.howFundsUtilizedAr.trim() === "",
        disclaimerEn: additionalInfo.disclaimerEn.trim() === "",
        disclaimerAr: additionalInfo.disclaimerAr.trim() === "",
        feesAndChargesEn: additionalInfo.feesAndChargesEn.trim() === "",
        feesAndChargesAr: additionalInfo.feesAndChargesAr.trim() === "",
        competitiveAdvantageEn:
          additionalInfo.competitiveAdvantageEn.trim() === "",
        competitiveAdvantageAr:
          additionalInfo.competitiveAdvantageAr.trim() === "",
        categories: formData.categories.length === 0,
        offerIssuerId: formData.offerIssuerId === null,
        statusId: formData.statusId === null,
        termsOfAgreementChangesId: formData.termsOfAgreementChangesId === null,
        sectorId: additionalInfo.sectorId === null,
        fundUtilizationMainPurposes:
          formData.fundUtilizationMainPurposes.length === 0,
        isShariaCompliantId: additionalInfo.isShariaCompliantId === null,
        investedAmount:
          formData.investedAmount === 0 ||
          (formData.investedAmount !== null &&
            isNaN(Number(formData.investedAmount))),
        internalRateReturn:
          formData.internalRateReturn === 0 ||
          (formData.internalRateReturn !== null &&
            isNaN(Number(formData.internalRateReturn))),
        investorsCount:
          formData.investorsCount === 0 ||
          (formData.investorsCount !== null &&
            isNaN(Number(formData.investorsCount))),
        estimatedGrossNetProceeds:
          formData.estimatedGrossNetProceeds === 0 ||
          (formData.estimatedGrossNetProceeds !== null &&
            isNaN(Number(formData.estimatedGrossNetProceeds))) ||
          (formData.estimatedGrossNetProceeds !== 0 &&
            (formData.estimatedGrossNetProceeds < minimumDinarValue ||
              formData.estimatedGrossNetProceeds > maximumDinarValue)),
        securitiesNumber:
          offerIssuerInfo.securitiesNumber === 0 ||
          (offerIssuerInfo.securitiesNumber !== null &&
            isNaN(Number(offerIssuerInfo.securitiesNumber))),
        investorsNumberRestrictionId:
          offerIssuerInfo.investorsNumberRestrictionId === null,
        islamicShariaComplianceId:
          offerIssuerInfo.islamicShariaComplianceId === null,
        applicationDate:
          isDateLaterThanToday(offerIssuerInfo.applicationDate) ||
          offerIssuerInfo.applicationDate.trim() === "",
        activationDate:
          offerIssuerInfo.isCmaConfirmed &&
          offerIssuerInfo.activationDate.trim() === "",
        projectionsApplicable:
          offerIssuerInfo.projectionsApplicable.trim() === "",
        riskDetails: offerIssuerInfo.riskDetails.trim() === "",
        financialProjectionAssumptions:
          offerIssuerInfo.financialProjectionAssumptions.trim() === "",
        acquisitionPropertyAssetExplanation:
          formData.acquisitionPropertyAssetExplanation.trim() === "",
        capitalExpenditureExplanation:
          formData.capitalExpenditureExplanation.trim() === "",
        generalDayCapitalExplanation:
          formData.generalDayCapitalExplanation.trim() === "",
        offerExpensesExplanation:
          formData.offerExpensesExplanation.trim() === "",
        commissionBrokerageFeesExplanation:
          formData.commissionBrokerageFeesExplanation.trim() === "",
        otherExplanation: formData.otherExplanation.trim() === "",
        cancelReason:
          formData.statusId === StatusValues.Cancelled &&
          formData.cancelReason.trim() === "",
        almouwaziConfirmationDate:
          offerIssuerInfo.isAlmouwaziConfirmed &&
          offerIssuerInfo.almouwaziConfirmationDate.trim() === "",
        detailsFinalizingDate:
          offerIssuerInfo.isAicConfirmed &&
          offerIssuerInfo.detailsFinalizingDate.trim() === "",
        cmaSubmissionDate:
          offerIssuerInfo.isCmaConfirmed &&
          offerIssuerInfo.cmaSubmissionDate.trim() === "",
      };
      setTouched(touchedFields);
      toast.error(`${VALIDATION_ERROR_FIELDS_REQUIRED_MESSAGE}`);
      return;
    }

    const hasImageFile = formData.files.some((file) => file.typeId === 1);
    if (!hasImageFile) {
      toast.error(VALIDATION_IMAGE_FILES);
      return;
    }

    if (
      (offerIssuerInfo.isAuditorReportsProvided &&
        offerIssuerInfo.auditorReportsDocument === null) ||
      (offerIssuerInfo.isFinPositionStatementProvided &&
        offerIssuerInfo.finPositionStatementDocument === null) ||
      (offerIssuerInfo.isFinancialNotesStatementProvided &&   offerIssuerInfo.financialNotesStatementDocument === null) ||
      (offerIssuerInfo.isRealEstateEvaluationProvided &&   offerIssuerInfo.realEstateEvaluationDocument === null) ||
      (offerIssuerInfo.isEquityChangesStatementProvided &&
        offerIssuerInfo.equityChangesStatementDocument === null) ||
      (offerIssuerInfo.isCashFlowStatementProvided &&
        offerIssuerInfo.cashFlowStatementDocument === null) ||
      (offerIssuerInfo.isIncomeStatementProvided &&
        offerIssuerInfo.incomeStatementDocument === null)
    ) {
      setSelectedTab(3);
      toast.error(VALIDATION_ATTACHED_FILES);
      return;
    }

    const formatDate = (date: string): string => {
      if (!date) return "";
      const [day, month, year] = date.split("/");
      return `${day}/${month}/${year}`;
    };

    const formattedFormData = {
      ...formData,
      startDate: formatDate(formData.startDate),
      endDate: formatDate(formData.endDate),
      collectFundsDeadline: formatDate(formData.collectFundsDeadline),
      securitiesAllotmentDate: formatDate(formData.securitiesAllotmentDate),
      additionalInfo,
      offerIssuerInfo: {
        ...offerIssuerInfo,
        almouwaziConfirmationDate: formatDate(
          offerIssuerInfo.almouwaziConfirmationDate
        ),
        cmaSubmissionDate: formatDate(offerIssuerInfo.cmaSubmissionDate),
        detailsFinalizingDate: formatDate(offerIssuerInfo.detailsFinalizingDate ),
        applicationDate: formatDate(offerIssuerInfo.applicationDate),
        activationDate: formatDate(offerIssuerInfo.activationDate),
      },
    };
    dispatch(saveOpportunity({ data: formattedFormData }))
      .unwrap()
      .then((response: any) => {
        const { success, warningFlag, message } = response;
        if (success) {
          toast.success(`${SAVE_OPPORTUNITY}`);
          navigate("/Opportunities");
        } else {
          if (warningFlag) {
            setWarningOfferIssuer(true);
            setSelectedTab(1);
          }
          toast.error(`${message}`);
        }
      })
      .catch((error) => {
        console.error("Failed to save item:", error);
      });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setAdditionalInfo({ ...additionalInfo, [name]: checked });
  };

  const handleTextAreaChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleTabChange = (newValue: number) => {
    setSelectedTab(newValue);
  };

  const tabs = [
    { id: 1, label: "Main info" },
    { id: 2, label: "Additional info" },
    { id: 3, label: "Opportunity Offer Issuer" },
    { id: 4, label: "Content info" },
    { id: 5, label: "Documents" },
    ...(id ? [{ id: 6, label: "Confirmations" }] : []),
  ];

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const reader = new FileReader();

      reader.onload = () => {
        const binaryData = reader.result as string;
        const base64String = window.btoa(binaryData);
        setFileData({
          ...fileData,
          fileName: file.name.substr(0, file.name.lastIndexOf(".")),
          extension: file.name.split(".").pop()?.toLowerCase() ?? "",
          contentType: file.type,
          data: base64String,
          size: file.size,
          date: "",
        });
      };

      reader.readAsBinaryString(file);
    } else {
      setFileData({
        typeId: 0,
        type: "",
        fileName: "",
        fileUrl: "",
        date: "",
        extension: "",
        contentType: "",
        data: "",
        size: 0,
      });
    }
  };

  const getTypeId = (name: string): number => {
    switch (name) {
      case "auditorReportsDocument":
        return FileTypes.AuditorReports;
      case "finPositionStatementDocument":
        return FileTypes.FinPositionStatement;
      case "incomeStatementDocument":
        return FileTypes.IncomeStatement;
      case "equityChangesStatementDocument":
        return FileTypes.EquityChangesStatement;
      case "cashFlowStatementDocument":
        return FileTypes.CashFlowStatement;
      case "financialNotesStatementDocument":
        return FileTypes.FinancialNotesStatement;
      case "realEstateEvaluationDocument":
        return FileTypes.RealEstateEvaluationDocument;
      default:
        return 0;
    }
  };

  const handleAttachFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    name: string
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (!allowedFileTypes.includes(file.type)) {
        toast.error(VALIDATION_ATTACHED_FILES_TYPES);
        return;
      }

      const reader = new FileReader();

      reader.onload = () => {
        const binaryData = reader.result as string;
        const base64String = window.btoa(binaryData);
        setOfferIssuerInfo((prev) => ({
          ...prev,
          [name]: {
            typeId: getTypeId(name),
            fileName: file.name.substr(0, file.name.lastIndexOf(".")),
            extension: file.name.split(".").pop()?.toLowerCase() ?? "",
            contentType: file.type,
            data: base64String,
            date: "",
          },
        }));
      };

      reader.readAsBinaryString(file);
    }
  };

  const validateFileExtension = (extension: any, allowedExtensions: any) => {
    return allowedExtensions.includes(extension);
  };

  const addFileEntry = () => {
    if (
      fileData.typeId === null ||
      fileData.typeId === 0 ||
      fileData.data === ""
    ) {
      toast.error(VALIDATION_FILES);
      return;
    }

    if (
      fileData.typeId === FileTypes.Cover ||
      fileData.typeId === FileTypes.Image
    ) {
      if (!validateFileExtension(fileData.extension, allowedImageExtensions)) {
        toast.error(VALIDATION_ATTACHED_IMAGE_TYPES);
        return;
      }
    } else if (fileData.typeId === FileTypes.Video) {
      if (!validateFileExtension(fileData.extension, allowedVideoExtensions)) {
        toast.error(VALIDATION_ATTACHED_VIDEO_TYPES);
        return;
      }
      if (fileData.size && fileData.size > MAX_VIDEO_SIZE_BYTES) {
        toast.error(`Video size must not exceed ${MAX_VIDEO_SIZE_MB} MB.`);
        return;
      }
    }

    const existingEntry = formData.files.some(
      (entry) => entry.typeId === FileTypes.Cover
    );

    if (fileData.typeId === FileTypes.Cover && existingEntry) {
      setPendingFile(fileData);
      setIsConfirmOpen(true);
    } else {
      proceedWithFileAddition(fileData);
    }
  };

  const proceedWithFileAddition = (fileData: any) => {
    const fileType = fileTypes.find((type: any) => type.id === fileData.typeId);
    const fileTypeText = fileType ? fileType.name : "";

    let newFilesEntries = formData.files.filter(
      (entry) =>
        entry.typeId !== FileTypes.Cover || fileData.typeId !== FileTypes.Cover
    );

    newFilesEntries = [...newFilesEntries, { ...fileData, type: fileTypeText }];
    setFormData((prevData) => ({ ...prevData, files: newFilesEntries }));

    resetFileData();
  };

  const resetFileData = () => {
    setFileData({
      typeId: 0,
      type: "",
      fileName: "",
      fileUrl: "",
      date: "",
      extension: "",
      contentType: "",
      data: "",
      size: 0,
    });
  };

  const handleConfirm = () => {
    if (pendingFile) {
      proceedWithFileAddition(pendingFile);
      setPendingFile(null);
      setIsConfirmOpen(false);
    }
  };

  const handleCancelConfirmation = () => {
    setPendingFile(null);
    setIsConfirmOpen(false);
  };

  const handleDownloadFile = (file: FileResource | null) => {
    if (file) {
      dispatch(getFile({ fileUrl: file.fileUrl || "" }));
    }
  };

  const handleFileClick = (file: FileResource | null) => {
    if (file) {
      const binaryData = atob(file.data);
      const len = binaryData.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryData.charCodeAt(i);
      }
      const contentType = file.contentType || "application/octet-stream";
      const blob = new Blob([bytes], { type: contentType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.fileName || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleFileDeleteClick = (row: any) => {
    setSelectedFileRow(row);
    setDeleteFileConfirmationOpen(true);
  };

  const removeFileEntry = () => {
    const row = selectedFileRow;
    const updatedEntries = formData.files.filter(
      (entry) =>
        !(entry.typeId === row?.typeId && entry.fileName === row?.fileName)
    );
    setFormData((prevData) => ({ ...prevData, files: updatedEntries }));
    setDeleteFileConfirmationOpen(false);
  };

  const FileColumns: CustomTableColumnsProps[] = [
    { id: "type", label: "Type", width: "150", align: "left", visible: true },
    {
      id: "fileName",
      label: "File Name",
      width: "150",
      align: "left",
      visible: true,
      renderCell: (params) => (
        <a href="#" onClick={() => handleDownloadFile(params.row)}>
          {params.row.fileName}
        </a>
      ),
    },
    {
      id: "actions",
      label: "Actions",
      width: "150",
      align: "center",
      visible: true,
      renderCell: (params) => (
        <div>
          <CustomIcon
            tooltipText="Delete"
            onClick={() => handleFileDeleteClick(params.row)}
            style={GeneralStyles.deleteIcon}
          >
            <DeleteIcon />
          </CustomIcon>
        </div>
      ),
    },
  ];

  const handleDeleteCancel = () => {
    setDeleteFileConfirmationOpen(false);
  };

  useEffect(() => {
    const validateDates = () => {
      if (formData.startDate && offerIssuerInfo.cmaSubmissionDate) {
        const startDate = parseDate(formData.startDate);
        const cmaSubmissionDate = parseDate(offerIssuerInfo.cmaSubmissionDate);
        if (startDate && cmaSubmissionDate) {
          const updatedDate = new Date(cmaSubmissionDate);
          updatedDate.setDate(updatedDate.getDate() + 5);
          if (startDate < updatedDate) {
            setFormData((prevData) => ({ ...prevData, startDate: "" }));
            setTouched((prevTouched) => ({
              ...prevTouched,
              startDate: true,
            }));
            toast.error(VALIDATION_CMA_START_DATE_MESSAGE);
            return;
          }
        }
      }
      if (formData.startDate && formData.endDate) {
        const startDate = parseDate(formData.startDate);
        const endDate = parseDate(formData.endDate);
        if (startDate && endDate) {
          if (startDate > endDate) {
            setFormData((prevData) => ({ ...prevData, endDate: "" }));
            setTouched((prevTouched) => ({
              ...prevTouched,
              startDate: true,
              endDate: true,
            }));
            toast.error(VALIDATION_ERROR_DATE_MESSAGE);
            return;
          }
          const dateDifference =
            (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
          if (dateDifference > 30) {
            setFormData((prevData) => ({ ...prevData, endDate: "" }));
            setTouched((prevTouched) => ({
              ...prevTouched,
              startDate: true,
              endDate: true,
            }));
            toast.error(VALIDATION_DATE_RANGE_MESSAGE);
            return;
          }
        }
      }
      if (formData.collectFundsDeadline && formData.endDate) {
        const collectFundsDeadline = parseDate(formData.collectFundsDeadline);
        const endDate = parseDate(formData.endDate);
        if (endDate && collectFundsDeadline) {
          if (endDate > collectFundsDeadline) {
            setFormData((prevData) => ({
              ...prevData,
              collectFundsDeadline: "",
            }));
            setTouched((prevTouched) => ({
              ...prevTouched,
              collectFundsDeadline: true,
              endDate: true,
            }));
            toast.error(VALIDATION_ERROR_DEADLINE_DATE_MESSAGE);
            return;
          }
        }
      }
      if (
        formData.securitiesAllotmentDate &&
        offerIssuerInfo.cmaSubmissionDate
      ) {
        const securitiesAllotmentDate = parseDate(
          formData.securitiesAllotmentDate
        );
        const cmaSubmissionDate = parseDate(offerIssuerInfo.cmaSubmissionDate);
        if (cmaSubmissionDate && securitiesAllotmentDate) {
          if (securitiesAllotmentDate < cmaSubmissionDate) {
            setFormData((prevData) => ({
              ...prevData,
              securitiesAllotmentDate: "",
            }));
            setTouched((prevTouched) => ({
              ...prevTouched,
              securitiesAllotmentDate: true,
            }));
            toast.error(VALIDATION_ERROR_ALLOTMENT_DATE_MESSAGE);
            return;
          }
        }
      }
    };

    validateDates();
  }, [
    formData.startDate,
    formData.endDate,
    formData.collectFundsDeadline,
    offerIssuerInfo.cmaSubmissionDate,
    formData.securitiesAllotmentDate,
  ]);

  useEffect(() => {
    const validateRange = () => {
      if (
        formData.targetAmount > formData.subscriptionMaxAmount ||
        formData.targetAmount < formData.subscriptionMinAmount
      ) {
        setTouched((prevTouched) => ({
          ...prevTouched,
          targetAmount: true,
        }));
      } else {
        setTouched((prevTouched) => ({
          ...prevTouched,
          targetAmount: false,
        }));
      }
    };

    validateRange();
  }, [
    formData.targetAmount,
    formData.subscriptionMinAmount,
    formData.subscriptionMaxAmount,
  ]);

  useEffect(() => {
    if (!offerIssuerInfo.isAlmouwaziConfirmed) {
      setOfferIssuerInfo({
        ...offerIssuerInfo,
        almouwaziConfirmationDate: "",
        almouwaziConfirmationComment: "",
        detailsFinalizingComment: "",
        cmaSubmissionComment: "",
        detailsFinalizingDate: "",
        cmaSubmissionDate: "",
      });
      setTouched((prevTouched) => ({
        ...prevTouched,
        almouwaziConfirmationDate: false,
        detailsFinalizingDate: false,
        cmaSubmissionDate: false,
        startDate: false,
        endDate: false,
        collectFundsDeadline: false,
        activationDate: false,
      }));
    } else if (!offerIssuerInfo.isAicConfirmed) {
      setOfferIssuerInfo({
        ...offerIssuerInfo,
        detailsFinalizingDate: "",
        detailsFinalizingComment: "",
        cmaSubmissionComment: "",
        cmaSubmissionDate: "",
      });
      setTouched((prevTouched) => ({
        ...prevTouched,
        detailsFinalizingDate: false,
        cmaSubmissionDate: false,
        startDate: false,
        endDate: false,
        collectFundsDeadline: false,
        activationDate: false,
      }));
    } else if (!offerIssuerInfo.isCmaConfirmed) {
      setOfferIssuerInfo({
        ...offerIssuerInfo,
        cmaSubmissionDate: "",
        cmaSubmissionComment: "",
        activationDate: "",
      });
      setFormData((prevData) => ({
        ...prevData,
        statusId: StatusValues.Draft,
      }));
      setTouched((prevTouched) => ({
        ...prevTouched,
        cmaSubmissionDate: false,
        startDate: false,
        endDate: false,
        collectFundsDeadline: false,
        activationDate: false,
      }));
    }
  }, [
    offerIssuerInfo.isAlmouwaziConfirmed,
    offerIssuerInfo.isCmaConfirmed,
    offerIssuerInfo.isAicConfirmed,
  ]);

  useEffect(() => { 
      if (
        formData.acquisitionPropertyAssetAmount > formData.subscriptionMaxAmount
      ) {
        setTouched((prevTouched) => ({
          ...prevTouched,
          acquisitionPropertyAssetAmount: true,
        }));
      } else {
        setTouched((prevTouched) => ({
          ...prevTouched,
          acquisitionPropertyAssetAmount: false,
        }));
      } 
  }, [
    formData.acquisitionPropertyAssetAmount, 
  ]);

  useEffect(() => { 
    if (
      formData.capitalExpenditureAmount > formData.subscriptionMaxAmount
    ) {
      setTouched((prevTouched) => ({
        ...prevTouched,
        capitalExpenditureAmount: true,
      }));
    } else {
      setTouched((prevTouched) => ({
        ...prevTouched,
        capitalExpenditureAmount: false,
      }));
    } 
}, [
  formData.capitalExpenditureAmount, 
  ]);

  useEffect(() => { 
    if (
      formData.generalDayCapitalAmount > formData.subscriptionMaxAmount
    ) {
      setTouched((prevTouched) => ({
        ...prevTouched,
        generalDayCapitalAmount: true,
      }));
    } else {
      setTouched((prevTouched) => ({
        ...prevTouched,
        generalDayCapitalAmount: false,
      }));
    } 
  }, [
  formData.generalDayCapitalAmount, 
  ]);

  useEffect(() => { 
    if (
      formData.offerExpensesAmount > formData.subscriptionMaxAmount
    ) {
      setTouched((prevTouched) => ({
        ...prevTouched,
        offerExpensesAmount: true,
      }));
    } else {
      setTouched((prevTouched) => ({
        ...prevTouched,
        offerExpensesAmount: false,
      }));
    } 
  }, [
  formData.offerExpensesAmount, 
  ]);

  useEffect(() => { 
    if (
      formData.commissionBrokerageFeesAmount > formData.subscriptionMaxAmount
    ) {
      setTouched((prevTouched) => ({
        ...prevTouched,
        commissionBrokerageFeesAmount: true,
      }));
    } else {
      setTouched((prevTouched) => ({
        ...prevTouched,
        commissionBrokerageFeesAmount: false,
      }));
    } 
  }, [
  formData.commissionBrokerageFeesAmount, 
  ]);

  useEffect(() => { 
    if (
      formData.otherAmount > formData.subscriptionMaxAmount
    ) {
      setTouched((prevTouched) => ({
        ...prevTouched,
        otherAmount: true,
      }));
    } else {
      setTouched((prevTouched) => ({
        ...prevTouched,
        otherAmount: false,
      }));
    } 
  }, [
  formData.otherAmount, 
  ]);
  
  useEffect(() => { 
    if (
      formData.subscriptionMaxAmount > formData.subscriptionMaxAmount
    ) {
      setTouched((prevTouched) => ({
        ...prevTouched,
        subscriptionMaxAmount: true,
      }));
    } else {
      setTouched((prevTouched) => ({
        ...prevTouched,
        subscriptionMaxAmount: false,
      }));
    } 
  }, [
  formData.subscriptionMaxAmount, 
  ]); 

  useEffect(() => {
    const validateCMARange = () => {
      if (
        offerIssuerInfo.applicationDate &&
        offerIssuerInfo.almouwaziConfirmationDate
      ) {
        const applicationDate = parseDate(offerIssuerInfo.applicationDate);
        const almouwaziConfirmationDate = parseDate(
          offerIssuerInfo.almouwaziConfirmationDate
        );
        if (almouwaziConfirmationDate && applicationDate) {
          if (
            almouwaziConfirmationDate < applicationDate ||
            almouwaziConfirmationDate > new Date()
          ) {
            setOfferIssuerInfo((prevData) => ({
              ...prevData,
              almouwaziConfirmationDate: "",
            }));
            setTouched((prevTouched) => ({
              ...prevTouched,
              almouwaziConfirmationDate: true,
            }));
            toast.error(VALIDATION_DETAILS_DATE_MESSAGE);
            return;
          }
        }
      }
      if (
        offerIssuerInfo.almouwaziConfirmationDate &&
        offerIssuerInfo.detailsFinalizingDate
      ) {
        const almouwaziConfirmationDate = parseDate(
          offerIssuerInfo.almouwaziConfirmationDate
        );
        const detailsFinalizingDate = parseDate(
          offerIssuerInfo.detailsFinalizingDate
        );
        if (detailsFinalizingDate && almouwaziConfirmationDate) {
          if (
            detailsFinalizingDate < almouwaziConfirmationDate ||
            detailsFinalizingDate > new Date()
          ) {
            setOfferIssuerInfo((prevData) => ({
              ...prevData,
              detailsFinalizingDate: "",
            }));
            setTouched((prevTouched) => ({
              ...prevTouched,
              detailsFinalizingDate: true,
            }));
            toast.error(VALIDATION_DETAILS_FINALIZING_DATE_MESSAGE);
            return;
          }
        }
      }
      if (
        offerIssuerInfo.cmaSubmissionDate &&
        offerIssuerInfo.detailsFinalizingDate
      ) {
        const cmaSubmissionDate = parseDate(offerIssuerInfo.cmaSubmissionDate);
        const detailsFinalizingDate = parseDate(
          offerIssuerInfo.detailsFinalizingDate
        );
        if (detailsFinalizingDate && cmaSubmissionDate) {
          if (
            cmaSubmissionDate < detailsFinalizingDate ||
            cmaSubmissionDate > new Date()
          ) {
            setOfferIssuerInfo((prevData) => ({
              ...prevData,
              cmaSubmissionDate: "",
            }));
            setTouched((prevTouched) => ({
              ...prevTouched,
              cmaSubmissionDate: true,
            }));
            toast.error(VALIDATION_CMA_SUBMISSION_DATE_MESSAGE);
            return;
          }
        }
      }
      if (offerIssuerInfo.cmaSubmissionDate && offerIssuerInfo.activationDate) {
        const cmaSubmissionDate = parseDate(offerIssuerInfo.cmaSubmissionDate);
        const activationDate = parseDate(offerIssuerInfo.activationDate);
        if (activationDate && cmaSubmissionDate) {
          if (activationDate < cmaSubmissionDate) {
            setOfferIssuerInfo((prevData) => ({
              ...prevData,
              activationDate: "",
            }));
            setTouched((prevTouched) => ({
              ...prevTouched,
              activationDate: true,
            }));
            toast.error(VALIDATION_ACTIVATION_DATE_MESSAGE);
            return;
          }
        }
      }
    };
    validateCMARange();
  }, [
    offerIssuerInfo.applicationDate,
    offerIssuerInfo.almouwaziConfirmationDate,
    offerIssuerInfo.detailsFinalizingDate,
    offerIssuerInfo.cmaSubmissionDate,
    offerIssuerInfo.activationDate,
  ]);

  const handleExport = () => {
    dispatch(exportOpportunity({ id: formData.id }));
  };

  useEffect(() => {
    if (formData.statusId !== StatusValues.Cancelled) {
      setFormData((prevData) => ({ ...prevData, cancelReason: "" }));
    }
  }, [formData.statusId]);

  useEffect(() => {
    if (opportunityDetails.blobData) {
      const url = window.URL.createObjectURL(opportunityDetails.blobData);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${formData.titleEn}.xlsx`);

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      dispatch(clearBlobData());
    }
  }, [opportunityDetails.blobData, dispatch]);

  useEffect(() => {
    if (file) {
      handleFileClick(file.data);
    }
  }, [file.data, dispatch]);

  useEffect(() => {
    if (!offerIssuerInfo.isAuditorReportsProvided) {
      setOfferIssuerInfo((prevData) => ({
        ...prevData,
        auditorReportsDocument: null,
      }));
    }
    if (!offerIssuerInfo.isFinPositionStatementProvided) {
      setOfferIssuerInfo((prevData) => ({
        ...prevData,
        finPositionStatementDocument: null,
      }));
    }
    if (!offerIssuerInfo.isIncomeStatementProvided) {
      setOfferIssuerInfo((prevData) => ({
        ...prevData,
        incomeStatementDocument: null,
      }));
    }
    if (!offerIssuerInfo.isEquityChangesStatementProvided) {
      setOfferIssuerInfo((prevData) => ({
        ...prevData,
        equityChangesStatementDocument: null,
      }));
    }
    if (!offerIssuerInfo.isCashFlowStatementProvided) {
      setOfferIssuerInfo((prevData) => ({
        ...prevData,
        cashFlowStatementDocument: null,
      }));
    }
    if (!offerIssuerInfo.isFinancialNotesStatementProvided) {
      setOfferIssuerInfo((prevData) => ({
        ...prevData,
        financialNotesStatementDocument: null,
      }));
    }
    if (!offerIssuerInfo.isRealEstateEvaluationProvided) {
      setOfferIssuerInfo((prevData) => ({
        ...prevData,
        realEstateEvaluationDocument: null,
      }));
    }
  }, [
    offerIssuerInfo.isAuditorReportsProvided,
    offerIssuerInfo.isFinPositionStatementProvided,
    offerIssuerInfo.isIncomeStatementProvided,
    offerIssuerInfo.isEquityChangesStatementProvided,
    offerIssuerInfo.isCashFlowStatementProvided,
    offerIssuerInfo.isFinancialNotesStatementProvided,
    offerIssuerInfo.isRealEstateEvaluationProvided,
  ]);

  return (
    <form style={GeneralStyles.formContainerStyle}>
      {opportunityDetails.isLoading && <Loader />}
      {opportunity.isLoading && <Loader />}
      <CustomDialog
        text="Are you sure you want to delete this item?"
        open={deleteFileConfirmationOpen}
        onClose={handleDeleteCancel}
        onConfirm={removeFileEntry}
        title="Confirm Deletion"
        buttonTitle="Delete"
        icon={<DeleteIcon />}
      />
      <CustomDialog
        open={isConfirmOpen}
        title="Replace Existing File"
        text="A cover image has already been uploaded."
        text2="Do you want to replace it?"
        onConfirm={handleConfirm}
        onClose={handleCancelConfirmation}
        buttonTitle="Replace"
        icon={<AutorenewIcon />}
      />

      <Grid
        container
        spacing={2}
        sx={{ flexDirection: { xs: "column", sm: "row" } }}
      >
        <Grid item xs={12} sm={6} md={formData.id !== 0 ? 8 : 10}>
          {id ? (
            <h1 style={GridStyles.title}>
              Edit Opportunity <b>{formData.titleEn}</b>
            </h1>
          ) : (
            <h1 style={GridStyles.title}>Add Opportunity</h1>
          )}
        </Grid>
        {formData.id !== 0 && (
          <Grid item xs={12} sm={6} md={2}>
            <div style={GridStyles.addButtonContainer}>
              <CustomButton
                type="button"
                style={GridStyles.downloadButton}
                onClick={handleExport}
              >
                <GetAppIcon style={GridStyles.iconStyle} /> Export
              </CustomButton>
            </div>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={2}>
          <div style={GridStyles.addButtonContainer}>
            <CustomButton
              type="button"
              style={GridStyles.addButton}
              onClick={handleSave}
            >
              <CheckCircleIcon style={GridStyles.iconStyle} /> Save
            </CustomButton>
          </div>
        </Grid>
        <Grid item xs={12} sm={12} md={12}>
          <CustomTabs
            selectedTab={selectedTab}
            handleTabChange={handleTabChange}
            tabs={tabs}
          />
          {selectedTab === 1 && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextField
                    label="Title (En)"
                    name="titleEn"
                    type="text"
                    value={formData.titleEn}
                    onChange={handleTextAreaChange}
                    required={true}
                    error={
                      touched.titleEn &&
                      (formData.titleEn.trim() === "" ||
                        formData.titleAr === formData.titleEn)
                    }
                    helperText={
                      touched.titleEn && formData.titleEn.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : touched.titleEn &&
                          formData.titleAr === formData.titleEn
                        ? `${VALIDATION_ERROR_TITLE_UNIQUE_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextField
                    label="Title (Ar)"
                    name="titleAr"
                    type="text"
                    value={formData.titleAr}
                    onChange={handleTextAreaChange}
                    required={true}
                    error={
                      touched.titleAr &&
                      (formData.titleAr.trim() === "" ||
                        formData.titleAr === formData.titleEn)
                    }
                    helperText={
                      touched.titleAr && formData.titleAr.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : touched.titleAr &&
                          formData.titleAr === formData.titleEn
                        ? `${VALIDATION_ERROR_TITLE_UNIQUE_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomDatePicker
                    value={offerIssuerInfo.applicationDate}
                    label="Date of application for this opportunity"
                    required={true}
                    onChange={(date: string) => {
                      setOfferIssuerInfo({
                        ...offerIssuerInfo,
                        applicationDate: date,
                      });
                      if (isDateLaterThanToday(date)) {
                        setTouched((prevData) => ({
                          ...prevData,
                          applicationDate: true,
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          applicationDate: false,
                        }));
                      }
                    }}
                    error={
                      touched.applicationDate &&
                      (isDateLaterThanToday(offerIssuerInfo.applicationDate) ||
                        offerIssuerInfo.applicationDate.trim() === "")
                    }
                    helperText={
                      touched.applicationDate &&
                      (isDateLaterThanToday(offerIssuerInfo.applicationDate) ||
                        offerIssuerInfo.applicationDate.trim() === "")
                        ? `${VALIDATION_ERROR_EARLIER_DATE_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomTextField
                    label="Invested Amount"
                    name="investedAmount"
                    type="text"
                    numericInput={true}
                    allowDecimal={true}
                    allowNegative={false}
                    unit="KD"
                    value={formatNumberWithCommas(formData.investedAmount)}
                    onChange={(e) => {
                      setWarningOfferIssuer(false);
                      const value = e.target.value.replace(/,/g, "");
                      if (!isNaN(Number(value))) {
                        setFormData((prevData) => ({
                          ...prevData,
                          investedAmount: parseFloat(value),
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          investedAmount: true,
                        }));
                      }
                    }}
                    required={true}
                    warningFlag={warningOfferIssuer}
                    error={
                      (touched.investedAmount &&
                        formData.investedAmount === 0) ||
                      (formData.investedAmount !== null &&
                        isNaN(Number(formData.investedAmount)))
                    }
                    helperText={
                      (touched.investedAmount &&
                        formData.investedAmount === 0) ||
                      (formData.investedAmount !== null &&
                        isNaN(Number(formData.investedAmount)))
                        ? `${VALIDATION_ERROR_INPUT_NUMBER_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomTextField
                    label="Investors Count"
                    name="investorsCount"
                    type="text"
                    numericInput={true}
                    allowDecimal={true}
                    allowNegative={false}
                    value={formData.investorsCount}
                    onChange={(e) => {
                      const { name, value } = e.target;
                      if (!isNaN(Number(value))) {
                        setFormData((prevData) => ({
                          ...prevData,
                          investorsCount: parseFloat(value),
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          investorsCount: true,
                        }));
                      }
                    }}
                    required={true}
                    error={
                      (touched.investorsCount &&
                        formData.investorsCount === 0) ||
                      (formData.investorsCount !== null &&
                        isNaN(Number(formData.investorsCount)))
                    }
                    helperText={
                      (touched.investorsCount &&
                        formData.investorsCount === 0) ||
                      (formData.investorsCount !== null &&
                        isNaN(Number(formData.internalRateReturn)))
                        ? `${VALIDATION_ERROR_INPUT_NUMBER_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomSelect
                    label="Related offer issuer"
                    name="offerIssuerId"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(offerIssuers.data) ? (
                        offerIssuers.data.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.name}
                          </MenuItem>
                        ))
                      ) : offerIssuers.data instanceof Object ? (
                        <MenuItem
                          key={(offerIssuers.data as any).id}
                          value={(offerIssuers.data as any).id}
                        >
                          {(offerIssuers.data as any).name}
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (Array.isArray(offerIssuers.data)) {
                          const selected = offerIssuers.data.filter(
                            (item: any) => value.includes(item.id)
                          );
                          return selected.map((item) => item.name).join(", ");
                        } else if (offerIssuers.data instanceof Object) {
                          return (offerIssuers.data as any).name || "";
                        }
                        return "";
                      },
                    }}
                    value={formData.offerIssuerId || ""}
                    onChange={(e) => {
                      setWarningOfferIssuer(false);
                      setFormData((prevData) => ({
                        ...prevData,
                        offerIssuerId: e.target.value as number,
                      }));
                    }}
                    multiple={false}
                    required={true}
                    warningFlag={warningOfferIssuer}
                    error={
                      touched.offerIssuerId && formData.offerIssuerId === null
                    }
                    helperText={
                      touched.offerIssuerId && formData.offerIssuerId === null
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid container item xs={12} sm={6} md={3} spacing={1}>
                  <Grid item xs={12} sm={6} md={8}>
                    <CustomTextField
                      label="Internal Rate Return"
                      name="internalRateReturn"
                      type="text"
                      numericInput={true}
                      allowDecimal={true}
                      allowNegative={false}
                      unit="KD"
                      value={formatNumberWithCommas(
                        formData.internalRateReturn
                      )}
                      onChange={(e: any) => {
                        const value = e.target.value.replace(/,/g, "");
                        if (!isNaN(Number(value))) {
                          setFormData((prevData) => ({
                            ...prevData,
                            internalRateReturn: parseFloat(value),
                          }));
                        } else {
                          setTouched((prevData) => ({
                            ...prevData,
                            internalRateReturn: true,
                          }));
                        }
                      }}
                      required={true}
                      error={
                        (touched.internalRateReturn &&
                          formData.internalRateReturn === 0) ||
                        (formData.internalRateReturn !== null &&
                          isNaN(Number(formData.internalRateReturn)))
                      }
                      helperText={
                        (touched.internalRateReturn &&
                          formData.internalRateReturn === 0) ||
                        (formData.internalRateReturn !== null &&
                          isNaN(Number(formData.internalRateReturn)))
                          ? `${VALIDATION_ERROR_INPUT_NUMBER_MESSAGE}`
                          : ""
                      }
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    container
                    alignItems="center"
                    justifyContent="center"
                    style={{ display: "flex" }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.isInternalRateReturnVisible}
                          style={GeneralStyles.checkBoxStyle}
                          onChange={(e: any) => {
                            const { name, checked } = e.target;
                            setFormData({
                              ...formData,
                              isInternalRateReturnVisible: checked,
                            });
                          }}
                          name="isInternalRateReturnVisible"
                        />
                      }
                      label="Visible IRR"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomSelect
                    label="Main purpose of fund utilization"
                    name="fundUtilizationMainPurposes"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(
                        mainPurposeOfFundUtilizationList.data
                      ) ? (
                        mainPurposeOfFundUtilizationList.data.map(
                          (item: any) => (
                            <MenuItem key={item.id} value={item.id}>
                              {item.valueEn}
                            </MenuItem>
                          )
                        )
                      ) : mainPurposeOfFundUtilizationList.data instanceof
                        Object ? (
                        <MenuItem
                          key={
                            (mainPurposeOfFundUtilizationList.data as any).id
                          }
                          value={
                            (mainPurposeOfFundUtilizationList.data as any).id
                          }
                        >
                          {
                            (mainPurposeOfFundUtilizationList.data as any)
                              .valueEn
                          }
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (
                          Array.isArray(mainPurposeOfFundUtilizationList.data)
                        ) {
                          const selected =
                            mainPurposeOfFundUtilizationList.data.filter(
                              (item: any) => value.includes(item.id)
                            );
                          return selected
                            .map((item) => item.valueEn)
                            .join(", ");
                        } else if (
                          mainPurposeOfFundUtilizationList.data instanceof
                          Object
                        ) {
                          return (
                            (mainPurposeOfFundUtilizationList.data as any)
                              .valueEn || ""
                          );
                        }
                        return "";
                      },
                    }}
                    value={formData.fundUtilizationMainPurposes || []}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        fundUtilizationMainPurposes: e.target.value as number[],
                      }))
                    }
                    multiple={true}
                    required={true}
                    error={
                      touched.fundUtilizationMainPurposes &&
                      formData.fundUtilizationMainPurposes.length === 0
                    }
                    helperText={
                      touched.fundUtilizationMainPurposes &&
                      formData.fundUtilizationMainPurposes.length === 0
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomSelect
                    label="Will there be any changes to the Terms of Agreement attached to the securities being offered?"
                    name="termsOfAgreementChangesId"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(
                        termsOfAgreementChangesList.data
                      ) ? (
                        termsOfAgreementChangesList.data.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.valueEn}
                          </MenuItem>
                        ))
                      ) : termsOfAgreementChangesList.data instanceof Object ? (
                        <MenuItem
                          key={(termsOfAgreementChangesList.data as any).id}
                          value={(termsOfAgreementChangesList.data as any).id}
                        >
                          {(termsOfAgreementChangesList.data as any).valueEn}
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (Array.isArray(termsOfAgreementChangesList.data)) {
                          const selected =
                            termsOfAgreementChangesList.data.filter(
                              (item: any) => value.includes(item.id)
                            );
                          return selected
                            .map((item) => item.valueEn)
                            .join(", ");
                        } else if (
                          termsOfAgreementChangesList.data instanceof Object
                        ) {
                          return (
                            (termsOfAgreementChangesList.data as any).valueEn ||
                            ""
                          );
                        }
                        return "";
                      },
                    }}
                    value={formData.termsOfAgreementChangesId || ""}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        termsOfAgreementChangesId: e.target.value as number,
                      }))
                    }
                    multiple={false}
                    required={true}
                    error={
                      touched.termsOfAgreementChangesId &&
                      formData.termsOfAgreementChangesId === null
                    }
                    helperText={
                      touched.termsOfAgreementChangesId &&
                      formData.termsOfAgreementChangesId === null
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomSelect
                    label="Ownership structure of the Eligible Issuer"
                    name="ownershipStructureId"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(ownershipstructureList.data) ? (
                        ownershipstructureList.data.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.valueEn}
                          </MenuItem>
                        ))
                      ) : ownershipstructureList.data instanceof Object ? (
                        <MenuItem
                          key={(ownershipstructureList.data as any).id}
                          value={(ownershipstructureList.data as any).id}
                        >
                          {(ownershipstructureList.data as any).valueEn}
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (Array.isArray(ownershipstructureList.data)) {
                          const selected = ownershipstructureList.data.filter(
                            (item: any) => value.includes(item.id)
                          );
                          return selected
                            .map((item) => item.valueEn)
                            .join(", ");
                        } else if (
                          ownershipstructureList.data instanceof Object
                        ) {
                          return (
                            (ownershipstructureList.data as any).valueEn || ""
                          );
                        }
                        return "";
                      },
                    }}
                    value={formData.ownershipStructureId || ""}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        ownershipStructureId: e.target.value as number,
                      }))
                    }
                    multiple={false}
                    required={true}
                    error={
                      touched.ownershipStructureId &&
                      formData.ownershipStructureId === null
                    }
                    helperText={
                      touched.ownershipStructureId &&
                      formData.ownershipStructureId === null
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomDatePicker
                    value={formData.startDate}
                    label="Start Date"
                    required={offerIssuerInfo.isCmaConfirmed ? true : false}
                    disabled={!offerIssuerInfo.isCmaConfirmed ? true : false}
                    onChange={(date: string) => {
                      setFormData({ ...formData, startDate: date });
                    }}
                    error={
                      touched.startDate && formData.startDate.trim() === ""
                    }
                    helperText={
                      touched.startDate && formData.startDate.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomDatePicker
                    value={formData.endDate}
                    label="End date"
                    required={offerIssuerInfo.isCmaConfirmed ? true : false}
                    disabled={!offerIssuerInfo.isCmaConfirmed ? true : false}
                    onChange={(date: string) => {
                      setFormData({ ...formData, endDate: date });
                    }}
                    error={touched.endDate && formData.endDate.trim() === ""}
                    helperText={
                      touched.endDate && formData.endDate.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomDatePicker
                    value={formData.collectFundsDeadline}
                    label="Deadline/s for the Issuer to collect the Funds "
                    required={offerIssuerInfo.isCmaConfirmed ? true : false}
                    disabled={!offerIssuerInfo.isCmaConfirmed ? true : false}
                    onChange={(date: string) => {
                      setFormData({ ...formData, collectFundsDeadline: date });
                    }}
                    error={
                      touched.collectFundsDeadline &&
                      formData.collectFundsDeadline.trim() === ""
                    }
                    helperText={
                      touched.collectFundsDeadline &&
                      formData.collectFundsDeadline.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomDatePicker
                    value={formData.securitiesAllotmentDate}
                    label="Date for allotment of Securities"
                    required={false}
                    disabled={!offerIssuerInfo.isCmaConfirmed ? true : false}
                    onChange={(date: string) =>
                      setFormData({
                        ...formData,
                        securitiesAllotmentDate: date,
                      })
                    }
                    error={
                      touched.securitiesAllotmentDate &&
                      formData.securitiesAllotmentDate.trim() === ""
                    }
                    helperText={
                      touched.securitiesAllotmentDate &&
                      formData.securitiesAllotmentDate.trim() === ""
                        ? `${VALIDATION_ERROR_ALLOTMENT_DATE_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextField
                    label="Estimated gross net proceeds to be received by the Offer issuer?"
                    name="estimatedGrossNetProceeds"
                    type="text"
                    numericInput={true}
                    allowDecimal={true}
                    allowNegative={false}
                    value={formData.estimatedGrossNetProceeds}
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, "");
                      if (!isNaN(Number(value))) {
                        setFormData((prevData) => ({
                          ...prevData,
                          estimatedGrossNetProceeds: parseFloat(value),
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          estimatedGrossNetProceeds: true,
                        }));
                      }
                    }}
                    required={true}
                    unit="KD"
                    error={
                      (touched.estimatedGrossNetProceeds &&
                        formData.estimatedGrossNetProceeds === 0) ||
                      (formData.estimatedGrossNetProceeds !== null &&
                        isNaN(Number(formData.estimatedGrossNetProceeds))) ||
                      (formData.estimatedGrossNetProceeds !== 0 &&
                        (formData.estimatedGrossNetProceeds <
                          minimumDinarValue ||
                          formData.estimatedGrossNetProceeds >
                            maximumDinarValue))
                    }
                    helperText={
                      (touched.estimatedGrossNetProceeds &&
                        formData.estimatedGrossNetProceeds === 0) ||
                      (formData.estimatedGrossNetProceeds !== null &&
                        isNaN(Number(formData.estimatedGrossNetProceeds)))
                        ? `${VALIDATION_ERROR_INPUT_NUMBER_MESSAGE}`
                        : formData.estimatedGrossNetProceeds !== 0 &&
                          (formData.estimatedGrossNetProceeds <
                            minimumDinarValue ||
                            formData.estimatedGrossNetProceeds >
                              maximumDinarValue)
                        ? `${VALIDATION_ERROR_RANGE_AMOUNT_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextField
                    label="Number of Shares to be offered in the Eligible Offer"
                    name="eligibleOfferSharesNumber"
                    type="text"
                    numericInput={true}
                    allowDecimal={true}
                    allowNegative={false}
                    value={formData.eligibleOfferSharesNumber}
                    onChange={(e) => {
                      const { name, value } = e.target;
                      if (!isNaN(Number(value))) {
                        setFormData((prevData) => ({
                          ...prevData,
                          eligibleOfferSharesNumber: parseFloat(value),
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          eligibleOfferSharesNumber: true,
                        }));
                      }
                    }}
                    required={false}
                    error={
                      formData.eligibleOfferSharesNumber !== null &&
                      (isNaN(Number(formData.eligibleOfferSharesNumber)) ||
                        Number(formData.eligibleOfferSharesNumber) <
                          minimumNumberOfShares)
                    }
                    helperText={
                      formData.eligibleOfferSharesNumber !== null &&
                      isNaN(Number(formData.eligibleOfferSharesNumber))
                        ? `${VALIDATION_ERROR_INPUT_NUMBER_MESSAGE}`
                        : formData.eligibleOfferSharesNumber !== null &&
                          Number(formData.eligibleOfferSharesNumber) <
                            minimumNumberOfShares
                        ? `${VALIDATION_ERROR_REQUIRED_INPUT_RANGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextField
                    label="Percentage of Shares to be listed"
                    name="sharesPercentage"
                    type="text"
                    unit="%"
                    value={formData.sharesPercentage}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        sharesPercentage: e.target.value,
                      }))
                    }
                    required={false}
                    error={
                      isNaN(Number(formData.sharesPercentage)) ||
                      Number(formData.sharesPercentage) < 0 ||
                      Number(formData.sharesPercentage) > 100
                    }
                    helperText={
                      isNaN(Number(formData.sharesPercentage)) ||
                      Number(formData.sharesPercentage) < 0 ||
                      Number(formData.sharesPercentage) > 100
                        ? `${VALIDATION_ERROR_INPUT_NUMBER_INRANGE_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextField
                    label="Minimum amount of subscription"
                    name="subscriptionMinAmount"
                    type="text"
                    numericInput={true}
                    allowDecimal={true}
                    allowNegative={false}
                    unit="KD"
                    value={formatNumberWithCommas(
                      formData.subscriptionMinAmount
                    )}
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, "");
                      if (!isNaN(Number(value))) {
                        setFormData((prevData) => ({
                          ...prevData,
                          subscriptionMinAmount: parseFloat(value),
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          subscriptionMinAmount: true,
                        }));
                      }
                    }}
                    required={true}
                    error={
                      (touched.subscriptionMinAmount &&
                        formData.subscriptionMinAmount === 0) ||
                      (formData.subscriptionMinAmount !== null &&
                        isNaN(Number(formData.subscriptionMinAmount)))
                    }
                    helperText={
                      (touched.subscriptionMinAmount &&
                        formData.subscriptionMinAmount === 0) ||
                      (formData.subscriptionMinAmount !== null &&
                        isNaN(Number(formData.subscriptionMinAmount)))
                        ? `${VALIDATION_ERROR_INPUT_NUMBER_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextField
                    label="Maximum amount of subscription"
                    name="subscriptionMaxAmount"
                    type="text"
                    numericInput={true}
                    allowDecimal={true}
                    allowNegative={false}
                    unit="KD"
                    value={formatNumberWithCommas(
                      formData.subscriptionMaxAmount
                    )}
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, "");
                      if (!isNaN(Number(value))) {
                        if (parseFloat(value) > maximumTargetAmount) {
                          setTouched((prevData) => ({
                            ...prevData,
                            subscriptionMaxAmount: true,
                          }));
                        }
                        setFormData((prevData) => ({
                          ...prevData,
                          subscriptionMaxAmount: parseFloat(value),
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          subscriptionMaxAmount: true,
                        }));
                      }
                    }}
                    required={true}
                    error={
                      (touched.subscriptionMaxAmount &&
                        formData.subscriptionMaxAmount === 0) ||
                      (formData.subscriptionMaxAmount !== null &&
                        isNaN(Number(formData.subscriptionMaxAmount))) ||
                      (formData.subscriptionMaxAmount !== null &&
                        formData.subscriptionMaxAmount > maximumTargetAmount)
                    }
                    helperText={
                      (touched.subscriptionMaxAmount &&
                        formData.subscriptionMaxAmount === 0) ||
                      (touched.subscriptionMaxAmount &&
                        formData.subscriptionMaxAmount !== null &&
                        isNaN(Number(formData.subscriptionMaxAmount)))
                        ? `${VALIDATION_ERROR_INPUT_NUMBER_MESSAGE}`
                        : touched.subscriptionMaxAmount &&
                          formData.subscriptionMaxAmount !== null &&
                          formData.subscriptionMaxAmount > maximumTargetAmount
                        ? `${VALIDATION_ERROR_MAXIMUM_TARGET_NUMBER_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextField
                    label="Target offering amount to be raised"
                    name="targetAmount"
                    type="text"
                    numericInput={true}
                    allowDecimal={true}
                    allowNegative={false}
                    unit="KD"
                    value={formatNumberWithCommas(formData.targetAmount)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, "");
                      if (!isNaN(Number(value))) {
                        setFormData((prevData) => ({
                          ...prevData,
                          targetAmount: parseFloat(value),
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          targetAmount: true,
                        }));
                      }
                    }}
                    required={true}
                    error={
                      (touched.targetAmount && formData.targetAmount !== 0) ||
                      (touched.targetAmount && formData.targetAmount === 0) ||
                      (formData.targetAmount !== null &&
                        isNaN(Number(formData.targetAmount)))
                    }
                    helperText={
                      (touched.targetAmount && formData.targetAmount === 0) ||
                      (formData.targetAmount !== null &&
                        isNaN(Number(formData.targetAmount)))
                        ? `${VALIDATION_ERROR_INPUT_NUMBER_MESSAGE}`
                        : touched.targetAmount && formData.targetAmount !== 0
                        ? `${VALIDATION_ERROR_TARGET_BETWEEN_RANGE_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextArea
                    label="Explain the purpose of the fundraising in detail (En)"
                    name="fundraisingPurposeEn"
                    value={formData.fundraisingPurposeEn}
                    onChange={handleTextAreaChange}
                    required={true}
                    error={
                      touched.fundraisingPurposeEn &&
                      formData.fundraisingPurposeEn.trim() === ""
                    }
                    helperText={
                      touched.fundraisingPurposeEn &&
                      formData.fundraisingPurposeEn.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextArea
                    label="Explain the purpose of the fundraising in detail (Ar)"
                    name="fundraisingPurposeAr"
                    value={formData.fundraisingPurposeAr}
                    onChange={handleTextAreaChange}
                    required={true}
                    error={
                      touched.fundraisingPurposeAr &&
                      formData.fundraisingPurposeAr.trim() === ""
                    }
                    helperText={
                      touched.fundraisingPurposeAr &&
                      formData.fundraisingPurposeAr.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextArea
                    label="Changes Details"
                    name="changesDetails"
                    value={formData.changesDetails}
                    onChange={handleTextAreaChange}
                    required={false}
                  />
                </Grid>
              </Grid>
            </>
          )}
          {selectedTab === 2 && (
            <>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Who is involved? <span style={{ color: "red" }}>*</span>
              </Typography>
              {/* {(touched.checkboxes && (!additionalInfo.isOurEmployeesInvolved || !additionalInfo.isDirectorsInvolved)) && (<span style={{ color: 'red' }}> {VALIDATION_ERROR_REQUIRED_MESSAGE}</span>)}    */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={additionalInfo.isOurEmployeesInvolved}
                        style={GeneralStyles.checkBoxStyle}
                        onChange={handleCheckboxChange}
                        name="isOurEmployeesInvolved"
                      />
                    }
                    label="Our employees involved"
                    style={{
                      cursor: "pointer",
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={additionalInfo.isDirectorsInvolved}
                        style={GeneralStyles.checkBoxStyle}
                        onChange={handleCheckboxChange}
                        name="isDirectorsInvolved"
                      />
                    }
                    label="Directors Involved"
                    style={{
                      cursor: "pointer",
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={additionalInfo.isOurShareholdersInvolved}
                        style={GeneralStyles.checkBoxStyle}
                        onChange={handleCheckboxChange}
                        name="isOurShareholdersInvolved"
                      />
                    }
                    label="Our shareholders Involved"
                    style={{
                      cursor: "pointer",
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomSelect
                    label="Categories"
                    name="categories"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(categoriesList.data) ? (
                        categoriesList.data.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.valueEn}
                          </MenuItem>
                        ))
                      ) : categoriesList.data instanceof Object ? (
                        <MenuItem
                          key={(categoriesList.data as any).id}
                          value={(categoriesList.data as any).id}
                        >
                          {(categoriesList.data as any).valueEn}
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (Array.isArray(categoriesList.data)) {
                          const selected = categoriesList.data.filter(
                            (item: any) => value.includes(item.id)
                          );
                          return selected
                            .map((item) => item.valueEn)
                            .join(", ");
                        } else if (categoriesList.data instanceof Object) {
                          return (categoriesList.data as any).valueEn || "";
                        }
                        return "";
                      },
                    }}
                    value={formData.categories || []}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        categories: e.target.value as number[],
                      }))
                    }
                    multiple={true}
                    required={true}
                    error={
                      touched.categories && formData.categories.length === 0
                    }
                    helperText={
                      touched.categories && formData.categories.length === 0
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomSelect
                    label="Is the company Sharia's Compliant "
                    name="isShariaCompliantId"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(
                        companyShariaaCompilantList.data
                      ) ? (
                        companyShariaaCompilantList.data.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.valueEn}
                          </MenuItem>
                        ))
                      ) : companyShariaaCompilantList.data instanceof Object ? (
                        <MenuItem
                          key={(companyShariaaCompilantList.data as any).id}
                          value={(companyShariaaCompilantList.data as any).id}
                        >
                          {(companyShariaaCompilantList.data as any).valueEn}
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (Array.isArray(companyShariaaCompilantList.data)) {
                          const selected =
                            companyShariaaCompilantList.data.filter(
                              (item: any) => value.includes(item.id)
                            );
                          return selected
                            .map((item) => item.valueEn)
                            .join(", ");
                        } else if (
                          companyShariaaCompilantList.data instanceof Object
                        ) {
                          return (
                            (companyShariaaCompilantList.data as any).valueEn ||
                            ""
                          );
                        }
                        return "";
                      },
                    }}
                    value={additionalInfo.isShariaCompliantId || ""}
                    onChange={(e) =>
                      setAdditionalInfo((prevData) => ({
                        ...prevData,
                        isShariaCompliantId: e.target.value as number,
                      }))
                    }
                    multiple={false}
                    required={true}
                    error={
                      touched.isShariaCompliantId &&
                      additionalInfo.isShariaCompliantId === null
                    }
                    helperText={
                      touched.isShariaCompliantId &&
                      additionalInfo.isShariaCompliantId === null
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomSelect
                    label="Sector"
                    name="sectorId"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(sectorsList.data) ? (
                        sectorsList.data.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.valueEn}
                          </MenuItem>
                        ))
                      ) : sectorsList.data instanceof Object ? (
                        <MenuItem
                          key={(sectorsList.data as any).id}
                          value={(sectorsList.data as any).id}
                        >
                          {(sectorsList.data as any).valueEn}
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (Array.isArray(sectorsList.data)) {
                          const selected = sectorsList.data.filter(
                            (item: any) => value.includes(item.id)
                          );
                          return selected
                            .map((item) => item.valueEn)
                            .join(", ");
                        } else if (sectorsList.data instanceof Object) {
                          return (sectorsList.data as any).valueEn || "";
                        }
                        return "";
                      },
                    }}
                    value={additionalInfo.sectorId || ""}
                    onChange={(e) =>
                      setAdditionalInfo((prevData) => ({
                        ...prevData,
                        sectorId: e.target.value as number,
                      }))
                    }
                    multiple={false}
                    required={true}
                    error={touched.sectorId && additionalInfo.sectorId === null}
                    helperText={
                      touched.sectorId && additionalInfo.sectorId === null
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              {additionalInfo.isShariaCompliantId ===
                ShariaCompliantValues.Yes && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={6}>
                    <CustomTextArea
                      label="Sharia Opinion Statement (En)"
                      name="shariaOpinionStatementEn"
                      value={additionalInfo.shariaOpinionStatementEn}
                      onChange={(e) =>
                        setAdditionalInfo((prevData) => ({
                          ...prevData,
                          shariaOpinionStatementEn: e.target.value,
                        }))
                      }
                      required={
                        additionalInfo.isShariaCompliantId ===
                        ShariaCompliantValues.Yes
                      }
                      error={
                        touched.shariaOpinionStatementEn &&
                        additionalInfo.shariaOpinionStatementEn.trim() === ""
                      }
                      helperText={
                        touched.shariaOpinionStatementEn &&
                        additionalInfo.shariaOpinionStatementEn.trim() === ""
                          ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                          : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <CustomTextArea
                      label="Sharia Opinion Statement (Ar)"
                      name="shariaOpinionStatementAr"
                      value={additionalInfo.shariaOpinionStatementAr}
                      onChange={(e) =>
                        setAdditionalInfo((prevData) => ({
                          ...prevData,
                          shariaOpinionStatementAr: e.target.value,
                        }))
                      }
                      required={
                        additionalInfo.isShariaCompliantId ===
                        ShariaCompliantValues.Yes
                      }
                      error={
                        touched.shariaOpinionStatementAr &&
                        additionalInfo.shariaOpinionStatementAr.trim() === ""
                      }
                      helperText={
                        touched.shariaOpinionStatementAr &&
                        additionalInfo.shariaOpinionStatementAr.trim() === ""
                          ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                          : ""
                      }
                    />
                  </Grid>
                </Grid>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextField
                    label="Acquisition of specified property or another specified asset"
                    name="acquisitionPropertyAssetExplanation"
                    type="text"
                    value={formData.acquisitionPropertyAssetExplanation}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        acquisitionPropertyAssetExplanation: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.acquisitionPropertyAssetExplanation &&
                      formData.acquisitionPropertyAssetExplanation.trim() === ""
                    }
                    helperText={
                      touched.acquisitionPropertyAssetExplanation &&
                      formData.acquisitionPropertyAssetExplanation.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextField
                    label="Amount"
                    name="acquisitionPropertyAssetAmount"
                    type="text"
                    numericInput={true}
                    allowDecimal={true}
                    allowNegative={false}
                    unit="KD"
                    value={formatNumberWithCommas(
                      formData.acquisitionPropertyAssetAmount
                    )}
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, "");
                      if (!isNaN(Number(value))) {
                        setFormData((prevData) => ({
                          ...prevData,
                          acquisitionPropertyAssetAmount: parseFloat(value),
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          acquisitionPropertyAssetAmount: true,
                        }));
                      }
                    }}
                    required={true}
                    error={
                      (touched.acquisitionPropertyAssetAmount &&
                        formData.acquisitionPropertyAssetAmount !== 0) ||
                      (touched.acquisitionPropertyAssetAmount &&
                        formData.acquisitionPropertyAssetAmount === 0) ||
                      (formData.acquisitionPropertyAssetAmount !== null &&
                        isNaN(Number(formData.acquisitionPropertyAssetAmount)))
                    }
                    helperText={
                      (touched.acquisitionPropertyAssetAmount &&
                        formData.acquisitionPropertyAssetAmount === 0) ||
                      (formData.acquisitionPropertyAssetAmount !== null &&
                        isNaN(Number(formData.acquisitionPropertyAssetAmount)))
                        ? `${VALIDATION_ERROR_INPUT_NUMBER_MESSAGE}`
                        : touched.acquisitionPropertyAssetAmount &&
                          formData.acquisitionPropertyAssetAmount !== 0
                        ? `${VALIDATION_ERROR_BETWEEN_RANGE_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextField
                    label="Capital expenditure"
                    name="capitalExpenditureExplanation"
                    type="text"
                    value={formData.capitalExpenditureExplanation}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        capitalExpenditureExplanation: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.capitalExpenditureExplanation &&
                      formData.capitalExpenditureExplanation.trim() === ""
                    }
                    helperText={
                      touched.capitalExpenditureExplanation &&
                      formData.capitalExpenditureExplanation.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextField
                    label="Amount"
                    name="capitalExpenditureAmount"
                    type="text"
                    numericInput={true}
                    allowDecimal={true}
                    allowNegative={false}
                    unit="KD"
                    value={formatNumberWithCommas(
                      formData.capitalExpenditureAmount
                    )}
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, "");
                      if (!isNaN(Number(value))) {
                        setFormData((prevData) => ({
                          ...prevData,
                          capitalExpenditureAmount: parseFloat(value),
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          capitalExpenditureAmount: true,
                        }));
                      }
                    }}
                    required={true}
                    error={
                      (touched.capitalExpenditureAmount &&
                        formData.capitalExpenditureAmount !== 0) ||
                      (touched.capitalExpenditureAmount &&
                        formData.capitalExpenditureAmount === 0) ||
                      (formData.capitalExpenditureAmount !== null &&
                        isNaN(Number(formData.capitalExpenditureAmount)))
                    }
                    helperText={
                      (touched.capitalExpenditureAmount &&
                        formData.capitalExpenditureAmount === 0) ||
                      (formData.capitalExpenditureAmount !== null &&
                        isNaN(Number(formData.capitalExpenditureAmount)))
                        ? `${VALIDATION_ERROR_INPUT_NUMBER_MESSAGE}`
                        : touched.capitalExpenditureAmount &&
                          formData.capitalExpenditureAmount !== 0
                        ? `${VALIDATION_ERROR_BETWEEN_RANGE_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextField
                    label="General day capital i.e. operational day to expenses"
                    name="generalDayCapitalExplanation"
                    type="text"
                    value={formData.generalDayCapitalExplanation}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        generalDayCapitalExplanation: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.generalDayCapitalExplanation &&
                      formData.generalDayCapitalExplanation.trim() === ""
                    }
                    helperText={
                      touched.generalDayCapitalExplanation &&
                      formData.generalDayCapitalExplanation.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextField
                    label="Amount"
                    name="generalDayCapitalAmount"
                    type="text"
                    numericInput={true}
                    allowDecimal={true}
                    allowNegative={false}
                    unit="KD"
                    value={formatNumberWithCommas(
                      formData.generalDayCapitalAmount
                    )}
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, "");
                      if (!isNaN(Number(value))) {
                        setFormData((prevData) => ({
                          ...prevData,
                          generalDayCapitalAmount: parseFloat(value),
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          generalDayCapitalAmount: true,
                        }));
                      }
                    }}
                    required={true}
                    error={
                      (touched.generalDayCapitalAmount &&
                        formData.generalDayCapitalAmount !== 0) ||
                      (touched.generalDayCapitalAmount &&
                        formData.generalDayCapitalAmount === 0) ||
                      (formData.generalDayCapitalAmount !== null &&
                        isNaN(Number(formData.generalDayCapitalAmount)))
                    }
                    helperText={
                      (touched.generalDayCapitalAmount &&
                        formData.generalDayCapitalAmount === 0) ||
                      (formData.generalDayCapitalAmount !== null &&
                        isNaN(Number(formData.generalDayCapitalAmount)))
                        ? `${VALIDATION_ERROR_INPUT_NUMBER_MESSAGE}`
                        : touched.generalDayCapitalAmount &&
                          formData.generalDayCapitalAmount !== 0
                        ? `${VALIDATION_ERROR_BETWEEN_RANGE_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextField
                    label="Expenses relating to the offer"
                    name="offerExpensesExplanation"
                    type="text"
                    value={formData.offerExpensesExplanation}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        offerExpensesExplanation: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.offerExpensesExplanation &&
                      formData.offerExpensesExplanation.trim() === ""
                    }
                    helperText={
                      touched.offerExpensesExplanation &&
                      formData.offerExpensesExplanation.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextField
                    label="Amount"
                    name="offerExpensesAmount"
                    type="text"
                    numericInput={true}
                    allowDecimal={true}
                    allowNegative={false}
                    unit="KD"
                    value={formatNumberWithCommas(formData.offerExpensesAmount)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, "");
                      if (!isNaN(Number(value))) {
                        setFormData((prevData) => ({
                          ...prevData,
                          offerExpensesAmount: parseFloat(value),
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          offerExpensesAmount: true,
                        }));
                      }
                    }}
                    required={true}
                    error={
                      (touched.offerExpensesAmount &&
                        formData.offerExpensesAmount !== 0) ||
                      (touched.offerExpensesAmount &&
                        formData.offerExpensesAmount === 0) ||
                      (formData.offerExpensesAmount !== null &&
                        isNaN(Number(formData.offerExpensesAmount)))
                    }
                    helperText={
                      (touched.offerExpensesAmount &&
                        formData.offerExpensesAmount === 0) ||
                      (formData.offerExpensesAmount !== null &&
                        isNaN(Number(formData.offerExpensesAmount)))
                        ? `${VALIDATION_ERROR_INPUT_NUMBER_MESSAGE}`
                        : touched.offerExpensesAmount &&
                          formData.offerExpensesAmount !== 0
                        ? `${VALIDATION_ERROR_BETWEEN_RANGE_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextField
                    label="Commission and brokerage fees"
                    name="commissionBrokerageFeesExplanation"
                    type="text"
                    value={formData.commissionBrokerageFeesExplanation}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        commissionBrokerageFeesExplanation: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.commissionBrokerageFeesExplanation &&
                      formData.commissionBrokerageFeesExplanation.trim() === ""
                    }
                    helperText={
                      touched.commissionBrokerageFeesExplanation &&
                      formData.commissionBrokerageFeesExplanation.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextField
                    label="Amount"
                    name="commissionBrokerageFeesAmount"
                    type="text"
                    numericInput={true}
                    allowDecimal={true}
                    allowNegative={false}
                    unit="KD"
                    value={formatNumberWithCommas(
                      formData.commissionBrokerageFeesAmount
                    )}
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, "");
                      if (!isNaN(Number(value))) {
                        setFormData((prevData) => ({
                          ...prevData,
                          commissionBrokerageFeesAmount: parseFloat(value),
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          commissionBrokerageFeesAmount: true,
                        }));
                      }
                    }}
                    required={true}
                    error={
                      (touched.commissionBrokerageFeesAmount &&
                        formData.commissionBrokerageFeesAmount === 0) ||
                      (formData.commissionBrokerageFeesAmount !== null &&
                        isNaN(
                          Number(formData.commissionBrokerageFeesAmount)
                        )) ||
                      (touched.commissionBrokerageFeesAmount &&
                        formData.commissionBrokerageFeesAmount !== 0)
                    }
                    helperText={
                      (touched.commissionBrokerageFeesAmount &&
                        formData.commissionBrokerageFeesAmount === 0) ||
                      (formData.commissionBrokerageFeesAmount !== null &&
                        isNaN(Number(formData.commissionBrokerageFeesAmount)))
                        ? `${VALIDATION_ERROR_INPUT_NUMBER_MESSAGE}`
                        : touched.commissionBrokerageFeesAmount &&
                          formData.commissionBrokerageFeesAmount !== 0
                        ? `${VALIDATION_ERROR_BETWEEN_RANGE_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextField
                    label="Other"
                    name="otherExplanation"
                    type="text"
                    value={formData.otherExplanation}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        otherExplanation: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.otherExplanation &&
                      formData.otherExplanation.trim() === ""
                    }
                    helperText={
                      touched.otherExplanation &&
                      formData.otherExplanation.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextField
                    label="Amount"
                    name="otherAmount"
                    type="text"
                    numericInput={true}
                    allowDecimal={true}
                    allowNegative={false}
                    unit="KD"
                    value={formatNumberWithCommas(formData.otherAmount)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, "");
                      if (!isNaN(Number(value))) {
                        setFormData((prevData) => ({
                          ...prevData,
                          otherAmount: parseFloat(value),
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          otherAmount: true,
                        }));
                      }
                    }}
                    required={true}
                    error={
                      (touched.otherAmount && formData.otherAmount === 0) ||
                      (formData.otherAmount !== null &&
                        isNaN(Number(formData.otherAmount))) ||
                      (touched.otherAmount && formData.otherAmount !== 0)
                    }
                    helperText={
                      (touched.otherAmount && formData.otherAmount === 0) ||
                      (formData.otherAmount !== null &&
                        isNaN(Number(formData.otherAmount)))
                        ? `${VALIDATION_ERROR_INPUT_NUMBER_MESSAGE}`
                        : touched.otherAmount && formData.otherAmount !== 0
                        ? `${VALIDATION_ERROR_BETWEEN_RANGE_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
            </>
          )}
          {selectedTab === 3 && (
            <>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Did the offer issuer provide the following documents{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid container spacing={2}>
                <Grid
                  container
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  spacing={1}
                  alignItems="center"
                >
                  <Grid
                    item
                    xs={12}
                    sm={offerIssuerInfo.isAuditorReportsProvided ? 10 : 12}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={offerIssuerInfo.isAuditorReportsProvided}
                          style={GeneralStyles.checkBoxStyle}
                          onChange={(e: any) => {
                            const { name, checked } = e.target;
                            setOfferIssuerInfo({
                              ...offerIssuerInfo,
                              isAuditorReportsProvided: checked,
                            });
                          }}
                          name="isAuditorReportsProvided"
                        />
                      }
                      label="Auditor Reports"
                      style={{
                        cursor: "pointer",
                      }}
                    />
                  </Grid>
                  {offerIssuerInfo.isAuditorReportsProvided && (
                    <>
                      {offerIssuerInfo.auditorReportsDocument && (
                        <Grid item xs={12} sm={1}>
                          <CustomIcon
                            tooltipText={
                              offerIssuerInfo.auditorReportsDocument.fileName ||
                              ""
                            }
                            onClick={() => {
                              handleFileClick(
                                offerIssuerInfo.auditorReportsDocument
                              );
                            }}
                            style={GridStyles.fileIcon}
                          >
                            <FileDownloadIcon />
                          </CustomIcon>
                        </Grid>
                      )}
                      <input
                        type="file"
                        ref={(el) =>
                          (fileInputRefs.current["auditorReportsDocument"] = el)
                        }
                        style={{ display: "none" }}
                        onChange={(e) =>
                          handleAttachFileChange(e, "auditorReportsDocument")
                        }
                      />
                      <Grid item xs={12} sm={1}>
                        <CustomIcon
                          tooltipText="Attach"
                          onClick={() =>
                            fileInputRefs.current[
                              "auditorReportsDocument"
                            ]?.click()
                          }
                          style={GridStyles.editIcon}
                        >
                          <AttachmentIcon />
                        </CustomIcon>
                      </Grid>
                    </>
                  )}
                </Grid>
                <Grid
                  container
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  spacing={1}
                  alignItems="center"
                >
                  <Grid
                    item
                    xs={12}
                    sm={
                      offerIssuerInfo.isFinPositionStatementProvided ? 10 : 12
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            offerIssuerInfo.isFinPositionStatementProvided
                          }
                          style={GeneralStyles.checkBoxStyle}
                          onChange={(e: any) => {
                            const { name, checked } = e.target;
                            setOfferIssuerInfo({
                              ...offerIssuerInfo,
                              isFinPositionStatementProvided: checked,
                            });
                          }}
                          name="isFinPositionStatementProvided"
                        />
                      }
                      label="Statement of fin position"
                      style={{
                        cursor: "pointer",
                      }}
                    />
                  </Grid>
                  {offerIssuerInfo.isFinPositionStatementProvided && (
                    <>
                      {offerIssuerInfo.finPositionStatementDocument && (
                        <Grid item xs={12} sm={1}>
                          <CustomIcon
                            tooltipText={
                              offerIssuerInfo.finPositionStatementDocument
                                .fileName || ""
                            }
                            onClick={() => {
                              handleFileClick(
                                offerIssuerInfo.finPositionStatementDocument
                              );
                            }}
                            style={GridStyles.fileIcon}
                          >
                            <FileDownloadIcon />
                          </CustomIcon>
                        </Grid>
                      )}
                      <input
                        type="file"
                        name="finPositionStatementDocument"
                        ref={(el) =>
                          (fileInputRefs.current[
                            "finPositionStatementDocument"
                          ] = el)
                        }
                        style={{ display: "none" }}
                        onChange={(e) =>
                          handleAttachFileChange(
                            e,
                            "finPositionStatementDocument"
                          )
                        }
                      />
                      <Grid item xs={12} sm={1}>
                        <CustomIcon
                          tooltipText="Attach"
                          onClick={() =>
                            fileInputRefs.current[
                              "finPositionStatementDocument"
                            ]?.click()
                          }
                          style={GridStyles.editIcon}
                        >
                          <AttachmentIcon />
                        </CustomIcon>
                      </Grid>
                    </>
                  )}
                </Grid>
                <Grid
                  container
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  spacing={1}
                  alignItems="center"
                >
                  <Grid
                    item
                    xs={12}
                    sm={offerIssuerInfo.isIncomeStatementProvided ? 10 : 12}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={offerIssuerInfo.isIncomeStatementProvided}
                          style={GeneralStyles.checkBoxStyle}
                          onChange={(e: any) => {
                            const { name, checked } = e.target;
                            setOfferIssuerInfo({
                              ...offerIssuerInfo,
                              isIncomeStatementProvided: checked,
                            });
                          }}
                          name="isIncomeStatementProvided"
                        />
                      }
                      label="Income Statement"
                      style={{
                        cursor: "pointer",
                      }}
                    />
                  </Grid>
                  {offerIssuerInfo.isIncomeStatementProvided && (
                    <>
                      {offerIssuerInfo.incomeStatementDocument && (
                        <Grid item xs={12} sm={1}>
                          <CustomIcon
                            tooltipText={
                              offerIssuerInfo.incomeStatementDocument
                                .fileName || ""
                            }
                            onClick={() => {
                              handleFileClick(
                                offerIssuerInfo.incomeStatementDocument
                              );
                            }}
                            style={GridStyles.fileIcon}
                          >
                            <FileDownloadIcon />
                          </CustomIcon>
                        </Grid>
                      )}
                      <input
                        type="file"
                        name="incomeStatementDocument"
                        ref={(el) =>
                          (fileInputRefs.current["incomeStatementDocument"] =
                            el)
                        }
                        style={{ display: "none" }}
                        onChange={(e) =>
                          handleAttachFileChange(e, "incomeStatementDocument")
                        }
                      />
                      <Grid item xs={12} sm={1}>
                        <CustomIcon
                          tooltipText="Attach"
                          onClick={() =>
                            fileInputRefs.current[
                              "incomeStatementDocument"
                            ]?.click()
                          }
                          style={GridStyles.editIcon}
                        >
                          <AttachmentIcon />
                        </CustomIcon>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid
                  container
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  spacing={1}
                  alignItems="center"
                >
                  <Grid
                    item
                    xs={12}
                    sm={
                      offerIssuerInfo.isEquityChangesStatementProvided ? 10 : 12
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            offerIssuerInfo.isEquityChangesStatementProvided
                          }
                          style={GeneralStyles.checkBoxStyle}
                          onChange={(e: any) => {
                            const { name, checked } = e.target;
                            setOfferIssuerInfo({
                              ...offerIssuerInfo,
                              isEquityChangesStatementProvided: checked,
                            });
                          }}
                          name="isEquityChangesStatementProvided"
                        />
                      }
                      label="Statement of changes in equity"
                      style={{
                        cursor: "pointer",
                      }}
                    />
                  </Grid>
                  {offerIssuerInfo.isEquityChangesStatementProvided && (
                    <>
                      {offerIssuerInfo.equityChangesStatementDocument && (
                        <Grid item xs={12} sm={1}>
                          <CustomIcon
                            tooltipText={
                              offerIssuerInfo.equityChangesStatementDocument
                                .fileName || ""
                            }
                            onClick={() => {
                              handleFileClick(
                                offerIssuerInfo.equityChangesStatementDocument
                              );
                            }}
                            style={GridStyles.fileIcon}
                          >
                            <FileDownloadIcon />
                          </CustomIcon>
                        </Grid>
                      )}
                      <input
                        type="file"
                        name="equityChangesStatementDocument"
                        ref={(el) =>
                          (fileInputRefs.current[
                            "equityChangesStatementDocument"
                          ] = el)
                        }
                        style={{ display: "none" }}
                        onChange={(e) =>
                          handleAttachFileChange(
                            e,
                            "equityChangesStatementDocument"
                          )
                        }
                      />
                      <Grid item xs={12} sm={1}>
                        <CustomIcon
                          tooltipText="Attach"
                          onClick={() =>
                            fileInputRefs.current[
                              "equityChangesStatementDocument"
                            ]?.click()
                          }
                          style={GridStyles.editIcon}
                        >
                          <AttachmentIcon />
                        </CustomIcon>
                      </Grid>
                    </>
                  )}
                </Grid>
                <Grid
                  container
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  spacing={1}
                  alignItems="center"
                >
                  <Grid
                    item
                    xs={12}
                    sm={offerIssuerInfo.isCashFlowStatementProvided ? 10 : 12}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={offerIssuerInfo.isCashFlowStatementProvided}
                          style={GeneralStyles.checkBoxStyle}
                          onChange={(e: any) => {
                            const { name, checked } = e.target;
                            setOfferIssuerInfo({
                              ...offerIssuerInfo,
                              isCashFlowStatementProvided: checked,
                            });
                          }}
                          name="isCashFlowStatementProvided"
                        />
                      }
                      label="Cash flow statement"
                      style={{
                        cursor: "pointer",
                      }}
                    />
                  </Grid>
                  {offerIssuerInfo.isCashFlowStatementProvided && (
                    <>
                      {offerIssuerInfo.cashFlowStatementDocument && (
                        <Grid item xs={12} sm={1}>
                          <CustomIcon
                            tooltipText={
                              offerIssuerInfo.cashFlowStatementDocument
                                .fileName || ""
                            }
                            onClick={() => {
                              handleFileClick(
                                offerIssuerInfo.cashFlowStatementDocument
                              );
                            }}
                            style={GridStyles.fileIcon}
                          >
                            <FileDownloadIcon />
                          </CustomIcon>
                        </Grid>
                      )}
                      <input
                        type="file"
                        name="cashFlowStatementDocument"
                        ref={(el) =>
                          (fileInputRefs.current["cashFlowStatementDocument"] =
                            el)
                        }
                        style={{ display: "none" }}
                        onChange={(e) =>
                          handleAttachFileChange(e, "cashFlowStatementDocument")
                        }
                      />
                      <Grid item xs={12} sm={1}>
                        <CustomIcon
                          tooltipText="Attach"
                          onClick={() =>
                            fileInputRefs.current[
                              "cashFlowStatementDocument"
                            ]?.click()
                          }
                          style={GridStyles.editIcon}
                        >
                          <AttachmentIcon />
                        </CustomIcon>
                      </Grid>
                    </>
                  )}
                </Grid>
                <Grid
                  container
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  spacing={1}
                  alignItems="center"
                >
                  <Grid
                    item
                    xs={12}
                    sm={
                      offerIssuerInfo.isFinancialNotesStatementProvided
                        ? 10
                        : 12
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            offerIssuerInfo.isFinancialNotesStatementProvided
                          }
                          style={GeneralStyles.checkBoxStyle}
                          onChange={(e: any) => {
                            const { name, checked } = e.target;
                            setOfferIssuerInfo({
                              ...offerIssuerInfo,
                              isFinancialNotesStatementProvided: checked,
                            });
                          }}
                          name="isFinancialNotesStatementProvided"
                        />
                      }
                      label="Notes on financial statement"
                      style={{
                        cursor: "pointer",
                      }}
                    />
                  </Grid>
                  {offerIssuerInfo.isFinancialNotesStatementProvided && (
                    <>
                      {offerIssuerInfo.financialNotesStatementDocument && (
                        <Grid item xs={12} sm={1}>
                          <CustomIcon
                            tooltipText={
                              offerIssuerInfo.financialNotesStatementDocument
                                .fileName || ""
                            }
                            onClick={() => {
                              handleFileClick(
                                offerIssuerInfo.financialNotesStatementDocument
                              );
                            }}
                            style={GridStyles.fileIcon}
                          >
                            <FileDownloadIcon />
                          </CustomIcon>
                        </Grid>
                      )}
                      <input
                        type="file"
                        name="financialNotesStatementDocument"
                        ref={(el) =>
                          (fileInputRefs.current[
                            "financialNotesStatementDocument"
                          ] = el)
                        }
                        style={{ display: "none" }}
                        onChange={(e) =>
                          handleAttachFileChange(
                            e,
                            "financialNotesStatementDocument"
                          )
                        }
                      />
                      <Grid item xs={12} sm={1}>
                        <CustomIcon
                          tooltipText="Attach"
                          onClick={() =>
                            fileInputRefs.current[
                              "financialNotesStatementDocument"
                            ]?.click()
                          }
                          style={GridStyles.editIcon}
                        >
                          <AttachmentIcon />
                        </CustomIcon>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
              {additionalInfo.sectorId === SectorValues.RealEstate && (
                    <Grid container spacing={2}>
                    <Grid
                      container
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      spacing={1}
                      alignItems="center"
                    >
                      <Grid
                        item
                        xs={12}
                        sm={
                          offerIssuerInfo.isRealEstateEvaluationProvided
                            ? 10
                            : 12
                        }
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                offerIssuerInfo.isRealEstateEvaluationProvided
                              }
                              style={GeneralStyles.checkBoxStyle}
                              onChange={(e: any) => {
                                const { name, checked } = e.target;
                                setOfferIssuerInfo({
                                  ...offerIssuerInfo,
                                  isRealEstateEvaluationProvided: checked,
                                });
                              }}
                              name="isRealEstateEvaluationProvided"
                            />
                          }
                          label="Real Estate Evaluation"
                          style={{
                            cursor: "pointer",
                          }}
                        />
                      </Grid>
                      {offerIssuerInfo.isRealEstateEvaluationProvided && (
                        <>
                          {offerIssuerInfo.realEstateEvaluationDocument && (
                            <Grid item xs={12} sm={1}>
                              <CustomIcon
                                tooltipText={
                                  offerIssuerInfo.realEstateEvaluationDocument
                                    .fileName || ""
                                }
                                onClick={() => {
                                  handleFileClick(
                                    offerIssuerInfo.realEstateEvaluationDocument
                                  );
                                }}
                                style={GridStyles.fileIcon}
                              >
                                <FileDownloadIcon />
                              </CustomIcon>
                            </Grid>
                          )}
                          <input
                            type="file"
                            name="realEstateEvaluationDocument"
                            ref={(el) =>
                              (fileInputRefs.current[
                                "realEstateEvaluationDocument"
                              ] = el)
                            }
                            style={{ display: "none" }}
                            onChange={(e) =>
                              handleAttachFileChange(
                                e,
                                "realEstateEvaluationDocument"
                              )
                            }
                          />
                          <Grid item xs={12} sm={1}>
                            <CustomIcon
                              tooltipText="Attach"
                              onClick={() =>
                                fileInputRefs.current[
                                  "realEstateEvaluationDocument"
                                ]?.click()
                              }
                              style={GridStyles.editIcon}
                            >
                              <AttachmentIcon />
                            </CustomIcon>
                          </Grid>
                        </>
                      )}
                    </Grid>
                    </Grid>
              )} 
              <Grid container spacing={2} mt={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextField
                    label="Number of Securities qualified for offer"
                    name="securitiesNumber"
                    type="text"
                    numericInput={true}
                    allowDecimal={true}
                    allowNegative={false}
                    value={offerIssuerInfo.securitiesNumber}
                    onChange={(e) => {
                      const { name, value } = e.target;
                      if (!isNaN(Number(value))) {
                        setOfferIssuerInfo((prevData) => ({
                          ...prevData,
                          securitiesNumber: parseFloat(value),
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          securitiesNumber: true,
                        }));
                      }
                    }}
                    required={true}
                    error={
                      (touched.securitiesNumber &&
                        offerIssuerInfo.securitiesNumber === 0) ||
                      (offerIssuerInfo.securitiesNumber !== null &&
                        isNaN(Number(offerIssuerInfo.securitiesNumber)))
                    }
                    helperText={
                      (touched.securitiesNumber &&
                        offerIssuerInfo.securitiesNumber === 0) ||
                      (offerIssuerInfo.securitiesNumber !== null &&
                        isNaN(Number(offerIssuerInfo.securitiesNumber)))
                        ? `${VALIDATION_ERROR_INPUT_NUMBER_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomSelect
                    label="Is there any restriction on the number of investors that can subscribe to this offer?"
                    name="investorsNumberRestrictionId"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(
                        investorsNumberRestrictionList.data
                      ) ? (
                        investorsNumberRestrictionList.data.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.valueEn}
                          </MenuItem>
                        ))
                      ) : investorsNumberRestrictionList.data instanceof
                        Object ? (
                        <MenuItem
                          key={(investorsNumberRestrictionList.data as any).id}
                          value={
                            (investorsNumberRestrictionList.data as any).id
                          }
                        >
                          {(investorsNumberRestrictionList.data as any).valueEn}
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (
                          Array.isArray(investorsNumberRestrictionList.data)
                        ) {
                          const selected =
                            investorsNumberRestrictionList.data.filter(
                              (item: any) => value.includes(item.id)
                            );
                          return selected
                            .map((item) => item.valueEn)
                            .join(", ");
                        } else if (
                          investorsNumberRestrictionList.data instanceof Object
                        ) {
                          return (
                            (investorsNumberRestrictionList.data as any)
                              .valueEn || ""
                          );
                        }
                        return "";
                      },
                    }}
                    value={offerIssuerInfo.investorsNumberRestrictionId || ""}
                    onChange={(e) =>
                      setOfferIssuerInfo((prevData) => ({
                        ...prevData,
                        investorsNumberRestrictionId: e.target.value as number,
                      }))
                    }
                    multiple={false}
                    required={true}
                    error={
                      touched.investorsNumberRestrictionId &&
                      offerIssuerInfo.investorsNumberRestrictionId === null
                    }
                    helperText={
                      touched.investorsNumberRestrictionId &&
                      offerIssuerInfo.investorsNumberRestrictionId === null
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomSelect
                    label="Does the company/ project comply with Islamic Sharia provisions?"
                    name="islamicShariaComplianceId"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(
                        islamicShariaComplianceList.data
                      ) ? (
                        islamicShariaComplianceList.data.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.valueEn}
                          </MenuItem>
                        ))
                      ) : islamicShariaComplianceList.data instanceof Object ? (
                        <MenuItem
                          key={(islamicShariaComplianceList.data as any).id}
                          value={(islamicShariaComplianceList.data as any).id}
                        >
                          {(islamicShariaComplianceList.data as any).valueEn}
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (Array.isArray(islamicShariaComplianceList.data)) {
                          const selected =
                            islamicShariaComplianceList.data.filter(
                              (item: any) => value.includes(item.id)
                            );
                          return selected
                            .map((item) => item.valueEn)
                            .join(", ");
                        } else if (
                          islamicShariaComplianceList.data instanceof Object
                        ) {
                          return (
                            (islamicShariaComplianceList.data as any).valueEn ||
                            ""
                          );
                        }
                        return "";
                      },
                    }}
                    value={offerIssuerInfo.islamicShariaComplianceId || ""}
                    onChange={(e) =>
                      setOfferIssuerInfo((prevData) => ({
                        ...prevData,
                        islamicShariaComplianceId: e.target.value as number,
                      }))
                    }
                    multiple={false}
                    required={true}
                    error={
                      touched.islamicShariaComplianceId &&
                      offerIssuerInfo.islamicShariaComplianceId === null
                    }
                    helperText={
                      touched.islamicShariaComplianceId &&
                      offerIssuerInfo.islamicShariaComplianceId === null
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={6}>
                  <CustomTextArea
                    label="Projections applicable to this project"
                    name="projectionsApplicable"
                    value={offerIssuerInfo.projectionsApplicable}
                    onChange={(e) =>
                      setOfferIssuerInfo((prevData) => ({
                        ...prevData,
                        projectionsApplicable: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.projectionsApplicable &&
                      offerIssuerInfo.projectionsApplicable.trim() === ""
                    }
                    helperText={
                      touched.projectionsApplicable &&
                      offerIssuerInfo.projectionsApplicable.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6}>
                  <CustomTextArea
                    label="Risk details"
                    name="riskDetails"
                    value={offerIssuerInfo.riskDetails}
                    onChange={(e) =>
                      setOfferIssuerInfo((prevData) => ({
                        ...prevData,
                        riskDetails: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.riskDetails &&
                      offerIssuerInfo.riskDetails.trim() === ""
                    }
                    helperText={
                      touched.riskDetails &&
                      offerIssuerInfo.riskDetails.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={6}>
                  <CustomTextArea
                    label="Assumptions for financial projection"
                    name="financialProjectionAssumptions"
                    value={offerIssuerInfo.financialProjectionAssumptions}
                    onChange={(e) =>
                      setOfferIssuerInfo((prevData) => ({
                        ...prevData,
                        financialProjectionAssumptions: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.financialProjectionAssumptions &&
                      offerIssuerInfo.financialProjectionAssumptions.trim() ===
                        ""
                    }
                    helperText={
                      touched.financialProjectionAssumptions &&
                      offerIssuerInfo.financialProjectionAssumptions.trim() ===
                        ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6}>
                  <CustomTextArea
                    label="Sharia opinion"
                    name="shariaOpinion"
                    value={offerIssuerInfo.shariaOpinion}
                    onChange={(e) =>
                      setOfferIssuerInfo((prevData) => ({
                        ...prevData,
                        shariaOpinion: e.target.value,
                      }))
                    }
                    required={false}
                    // error={touched.shariaOpinion && offerIssuerInfo.shariaOpinion.trim() === ''}
                    // helperText={touched.shariaOpinion && offerIssuerInfo.shariaOpinion.trim() === '' ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}` : ''}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextArea
                    label="The history of the Issuer (i.e. previous crowdfunding project and whether they were successful or not)"
                    name="issuerHistory"
                    value={formData.issuerHistory}
                    onChange={handleTextAreaChange}
                    required={true}
                    error={
                      touched.issuerHistory &&
                      formData.issuerHistory.trim() === ""
                    }
                    helperText={
                      touched.issuerHistory &&
                      formData.issuerHistory.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
            </>
          )}
          {selectedTab === 4 && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextArea
                    label="Summary (En)"
                    name="summaryEn"
                    value={additionalInfo.summaryEn}
                    onChange={(e) =>
                      setAdditionalInfo((prevData) => ({
                        ...prevData,
                        summaryEn: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.summaryEn &&
                      additionalInfo.summaryEn.trim() === ""
                    }
                    helperText={
                      touched.summaryEn &&
                      additionalInfo.summaryEn.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextArea
                    label="Summary (Ar)"
                    name="summaryAr"
                    value={additionalInfo.summaryAr}
                    onChange={(e) =>
                      setAdditionalInfo((prevData) => ({
                        ...prevData,
                        summaryAr: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.summaryAr &&
                      additionalInfo.summaryAr.trim() === ""
                    }
                    helperText={
                      touched.summaryAr &&
                      additionalInfo.summaryAr.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextArea
                    label="OurStory (En)"
                    name="ourStoryEn"
                    value={additionalInfo.ourStoryEn}
                    onChange={(e) =>
                      setAdditionalInfo((prevData) => ({
                        ...prevData,
                        ourStoryEn: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.ourStoryEn &&
                      additionalInfo.ourStoryEn.trim() === ""
                    }
                    helperText={
                      touched.ourStoryEn &&
                      additionalInfo.ourStoryEn.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextArea
                    label="OurStory (Ar)"
                    name="ourStoryAr"
                    value={additionalInfo.ourStoryAr}
                    onChange={(e) =>
                      setAdditionalInfo((prevData) => ({
                        ...prevData,
                        ourStoryAr: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.ourStoryAr &&
                      additionalInfo.ourStoryAr.trim() === ""
                    }
                    helperText={
                      touched.ourStoryAr &&
                      additionalInfo.ourStoryAr.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextArea
                    label="About the company (En)"
                    name="aboutTheCompanyEn"
                    value={additionalInfo.aboutTheCompanyEn}
                    onChange={(e) =>
                      setAdditionalInfo((prevData) => ({
                        ...prevData,
                        aboutTheCompanyEn: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.aboutTheCompanyEn &&
                      additionalInfo.aboutTheCompanyEn.trim() === ""
                    }
                    helperText={
                      touched.aboutTheCompanyEn &&
                      additionalInfo.aboutTheCompanyEn.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextArea
                    label="About the company (Ar)"
                    name="aboutTheCompanyAr"
                    value={additionalInfo.aboutTheCompanyAr}
                    onChange={(e) =>
                      setAdditionalInfo((prevData) => ({
                        ...prevData,
                        aboutTheCompanyAr: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.aboutTheCompanyAr &&
                      additionalInfo.aboutTheCompanyAr.trim() === ""
                    }
                    helperText={
                      touched.aboutTheCompanyAr &&
                      additionalInfo.aboutTheCompanyAr.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextArea
                    label="Risks about the company (En)"
                    name="riskEn"
                    value={additionalInfo.riskEn}
                    onChange={(e) =>
                      setAdditionalInfo((prevData) => ({
                        ...prevData,
                        riskEn: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.riskEn && additionalInfo.riskEn.trim() === ""
                    }
                    helperText={
                      touched.riskEn && additionalInfo.riskEn.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextArea
                    label="Risks about the company (Ar)"
                    name="riskAr"
                    value={additionalInfo.riskAr}
                    onChange={(e) =>
                      setAdditionalInfo((prevData) => ({
                        ...prevData,
                        riskAr: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.riskAr && additionalInfo.riskAr.trim() === ""
                    }
                    helperText={
                      touched.riskAr && additionalInfo.riskAr.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextArea
                    label="How the funds will be utilized (En)"
                    name="howFundsUtilizedEn"
                    value={additionalInfo.howFundsUtilizedEn}
                    onChange={(e) =>
                      setAdditionalInfo((prevData) => ({
                        ...prevData,
                        howFundsUtilizedEn: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.howFundsUtilizedEn &&
                      additionalInfo.howFundsUtilizedEn.trim() === ""
                    }
                    helperText={
                      touched.howFundsUtilizedEn &&
                      additionalInfo.howFundsUtilizedEn.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextArea
                    label="How the funds will be utilized (Ar)"
                    name="howFundsUtilizedAr"
                    value={additionalInfo.howFundsUtilizedAr}
                    onChange={(e) =>
                      setAdditionalInfo((prevData) => ({
                        ...prevData,
                        howFundsUtilizedAr: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.howFundsUtilizedAr &&
                      additionalInfo.howFundsUtilizedAr.trim() === ""
                    }
                    helperText={
                      touched.howFundsUtilizedAr &&
                      additionalInfo.howFundsUtilizedAr.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextArea
                    label="Competitive Advantage (En)"
                    name="competitiveAdvantageEn"
                    value={additionalInfo.competitiveAdvantageEn}
                    onChange={(e) =>
                      setAdditionalInfo((prevData) => ({
                        ...prevData,
                        competitiveAdvantageEn: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.competitiveAdvantageEn &&
                      additionalInfo.competitiveAdvantageEn.trim() === ""
                    }
                    helperText={
                      touched.competitiveAdvantageEn &&
                      additionalInfo.competitiveAdvantageEn.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextArea
                    label="Competitve Advantage (Ar)"
                    name="competitiveAdvantageAr"
                    value={additionalInfo.competitiveAdvantageAr}
                    onChange={(e) =>
                      setAdditionalInfo((prevData) => ({
                        ...prevData,
                        competitiveAdvantageAr: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.competitiveAdvantageAr &&
                      additionalInfo.competitiveAdvantageAr.trim() === ""
                    }
                    helperText={
                      touched.competitiveAdvantageAr &&
                      additionalInfo.competitiveAdvantageAr.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextArea
                    label="Fees And Charges (En)"
                    name="feesAndChargesEn"
                    value={additionalInfo.feesAndChargesEn}
                    onChange={(e) =>
                      setAdditionalInfo((prevData) => ({
                        ...prevData,
                        feesAndChargesEn: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.feesAndChargesEn &&
                      additionalInfo.feesAndChargesEn.trim() === ""
                    }
                    helperText={
                      touched.feesAndChargesEn &&
                      additionalInfo.feesAndChargesEn.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextArea
                    label="Fees And Charges (Ar)"
                    name="feesAndChargesAr"
                    value={additionalInfo.feesAndChargesAr}
                    onChange={(e) =>
                      setAdditionalInfo((prevData) => ({
                        ...prevData,
                        feesAndChargesAr: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.feesAndChargesAr &&
                      additionalInfo.feesAndChargesAr.trim() === ""
                    }
                    helperText={
                      touched.feesAndChargesAr &&
                      additionalInfo.feesAndChargesAr.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextArea
                    label="Disclaimer (En)"
                    name="disclaimerEn"
                    value={additionalInfo.disclaimerEn}
                    onChange={(e) =>
                      setAdditionalInfo((prevData) => ({
                        ...prevData,
                        disclaimerEn: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.disclaimerEn &&
                      additionalInfo.disclaimerEn.trim() === ""
                    }
                    helperText={
                      touched.disclaimerEn &&
                      additionalInfo.disclaimerEn.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextArea
                    label="Disclaimer (Ar)"
                    name="disclaimerAr"
                    value={additionalInfo.disclaimerAr}
                    onChange={(e) =>
                      setAdditionalInfo((prevData) => ({
                        ...prevData,
                        disclaimerAr: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.disclaimerAr &&
                      additionalInfo.disclaimerAr.trim() === ""
                    }
                    helperText={
                      touched.disclaimerAr &&
                      additionalInfo.disclaimerAr.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextArea
                    label="Other (En)"
                    name="othersEn"
                    value={additionalInfo.othersEn}
                    onChange={(e) =>
                      setAdditionalInfo((prevData) => ({
                        ...prevData,
                        othersEn: e.target.value,
                      }))
                    }
                    required={false}
                    error={
                      touched.othersEn && additionalInfo.othersEn.trim() === ""
                    }
                    helperText={
                      touched.othersEn && additionalInfo.othersEn.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomTextArea
                    label="Other (Ar)"
                    name="othersAr"
                    value={additionalInfo.othersAr}
                    onChange={(e) =>
                      setAdditionalInfo((prevData) => ({
                        ...prevData,
                        othersAr: e.target.value,
                      }))
                    }
                    required={false}
                    error={
                      touched.othersAr && additionalInfo.othersAr.trim() === ""
                    }
                    helperText={
                      touched.othersAr && additionalInfo.othersAr.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
            </>
          )}
          {selectedTab === 5 && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={5}>
                  <CustomSelect
                    label="File Type"
                    name="typeId"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(fileTypes) ? (
                        fileTypes.map((type: any) => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.name}
                          </MenuItem>
                        ))
                      ) : fileTypes ? (
                        <MenuItem
                          key={(fileTypes as any).id}
                          value={(fileTypes as any).id}
                        >
                          {(fileTypes as any).name}
                        </MenuItem>
                      ) : null,
                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (Array.isArray(fileTypes)) {
                          const selectedFile = fileTypes.filter((item: any) =>
                            value.includes(item.id)
                          );
                          return selectedFile
                            .map((item) => item.name)
                            .join(", ");
                        } else if (fileTypes) {
                          return (fileTypes as any).name || "";
                        }
                        return "";
                      },
                    }}
                    value={fileData.typeId || 0}
                    onChange={(e) =>
                      setFileData({
                        ...fileData,
                        typeId: e.target.value as number,
                      })
                    }
                    required={true}
                    multiple={false}
                    error={touched?.type && fileData.typeId === 0}
                    helperText={
                      touched?.type && fileData.typeId === 0
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={10} sm={10} md={5}>
                  <CustomFileInput
                    label="File"
                    name="file"
                    type="file"
                    value={fileData.fileName || ""}
                    onChange={handleFileChange}
                    required={true}
                    error={touched?.file}
                    helperText={
                      touched?.file
                        ? `${VALIDATION_ERROR_REQUIRED_WITH_CORRECT_FORMAT_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={2} sm={2} md={2}>
                  <CustomIcon
                    tooltipText="Add"
                    style={GeneralStyles.addIcon}
                    onClick={addFileEntry}
                  >
                    <AddIcon />
                  </CustomIcon>
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={12}>
                  <CustomPaginatedDataTable
                    columns={FileColumns}
                    count={formData.files.length}
                    data={formData.files}
                    usePaginatedData={true}
                  />
                </Grid>
              </Grid>
            </>
          )}
          {selectedTab === 6 && (
            <>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Date of application for this opportunity{" "}
                {offerIssuerInfo.applicationDate}
              </Typography>
              <Grid container spacing={2} mt={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={offerIssuerInfo.isAlmouwaziConfirmed}
                        style={GeneralStyles.checkBoxStyle}
                        onChange={(e: any) => {
                          const { name, checked } = e.target;
                          setOfferIssuerInfo({
                            ...offerIssuerInfo,
                            isAlmouwaziConfirmed: checked,
                            isAicConfirmed: checked
                              ? offerIssuerInfo.isAicConfirmed
                              : false,
                            isCmaConfirmed: checked
                              ? offerIssuerInfo.isCmaConfirmed
                              : false,
                          });
                        }}
                        name="isAlmouwaziConfirmed"
                      />
                    }
                    label="Confirmed by Almowazi Administration"
                    style={{
                      cursor: "pointer",
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomDatePicker
                    value={offerIssuerInfo.almouwaziConfirmationDate}
                    label="Date of submitting to Almowazi Administration for approval "
                    required={true}
                    disabled={!offerIssuerInfo.isAlmouwaziConfirmed}
                    onChange={(date: string) => {
                      setOfferIssuerInfo({
                        ...offerIssuerInfo,
                        almouwaziConfirmationDate: date,
                      });
                    }}
                    error={
                      touched.almouwaziConfirmationDate &&
                      offerIssuerInfo.almouwaziConfirmationDate.trim() === ""
                    }
                    helperText={
                      touched.almouwaziConfirmationDate &&
                      offerIssuerInfo.almouwaziConfirmationDate.trim() === ""
                        ? `${VALIDATION_DETAILS_DATE_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextField
                      label="Comment"
                      name="almouwaziConfirmationComment"
                      type="text"
                      value={offerIssuerInfo.almouwaziConfirmationComment}
                      onChange={(e) =>
                        setOfferIssuerInfo((prevData) => ({
                          ...prevData,
                          almouwaziConfirmationComment: e.target.value,
                        }))
                      }
                      required={false}
                      disabled={!offerIssuerInfo.isAlmouwaziConfirmed} 
                    />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={offerIssuerInfo.isAicConfirmed}
                        style={GeneralStyles.checkBoxStyle}
                        disabled={!offerIssuerInfo.isAlmouwaziConfirmed}
                        onChange={(e: any) => {
                          const { name, checked } = e.target;
                          setOfferIssuerInfo({
                            ...offerIssuerInfo,
                            isAicConfirmed: checked,
                            isCmaConfirmed: checked
                              ? offerIssuerInfo.isCmaConfirmed
                              : false,
                          });
                        }}
                        name="isAicConfirmed"
                      />
                    }
                    label="Confirmed by AIC"
                    style={{
                      cursor: "pointer",
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomDatePicker
                    value={offerIssuerInfo.detailsFinalizingDate}
                    label="Date of finalizing  details for this opportunity"
                    required={true}
                    onChange={(date: string) => {
                      setOfferIssuerInfo({
                        ...offerIssuerInfo,
                        detailsFinalizingDate: date,
                      });
                    }}
                    disabled={!offerIssuerInfo.isAicConfirmed}
                    error={
                      touched.detailsFinalizingDate &&
                      offerIssuerInfo.detailsFinalizingDate.trim() === ""
                    }
                    helperText={
                      touched.detailsFinalizingDate &&
                      offerIssuerInfo.detailsFinalizingDate.trim() === ""
                        ? `${VALIDATION_DETAILS_FINALIZING_DATE_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextField
                      label="Comment"
                      name="detailsFinalizingComment"
                      type="text"
                      value={offerIssuerInfo.detailsFinalizingComment}
                      onChange={(e) =>
                        setOfferIssuerInfo((prevData) => ({
                          ...prevData,
                          detailsFinalizingComment: e.target.value,
                        }))
                      }
                      required={false}
                      disabled={!offerIssuerInfo.isAicConfirmed}
                    />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={offerIssuerInfo.isCmaConfirmed}
                        style={GeneralStyles.checkBoxStyle}
                        disabled={!offerIssuerInfo.isAicConfirmed}
                        onChange={(e: any) => {
                          const { name, checked } = e.target;
                          setOfferIssuerInfo({
                            ...offerIssuerInfo,
                            isCmaConfirmed: checked,
                          });
                        }}
                        name="isCmaConfirmed"
                      />
                    }
                    label="Confirmed by CMA"
                    style={{
                      cursor: "pointer",
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomDatePicker
                    value={offerIssuerInfo.cmaSubmissionDate}
                    label="Date of submitting to CMA for approval "
                    required={true}
                    onChange={(date: string) => {
                      setOfferIssuerInfo({
                        ...offerIssuerInfo,
                        cmaSubmissionDate: date,
                      });
                    }}
                    disabled={!offerIssuerInfo.isCmaConfirmed}
                    error={
                      touched.cmaSubmissionDate &&
                      offerIssuerInfo.cmaSubmissionDate.trim() === ""
                    }
                    helperText={
                      touched.cmaSubmissionDate &&
                      offerIssuerInfo.cmaSubmissionDate.trim() === ""
                        ? `${VALIDATION_CMA_SUBMISSION_DATE_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextField
                      label="Comment"
                      name="cmaSubmissionComment"
                      type="text"
                      value={offerIssuerInfo.cmaSubmissionComment}
                      onChange={(e) =>
                        setOfferIssuerInfo((prevData) => ({
                          ...prevData,
                          cmaSubmissionComment: e.target.value,
                        }))
                      }
                      required={false}
                      disabled={!offerIssuerInfo.isCmaConfirmed}
                    />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid container item xs={12} sm={6} md={12} spacing={1}>
                  <Grid item xs={12} sm={6} md={6}>
                    <CustomSelect
                      label="Status"
                      name="statusId"
                      type="text"
                      SelectProps={{
                        children: Array.isArray(statusList.data) ? (
                          statusList.data.map((item: any) => (
                            <MenuItem key={item.id} value={item.id}>
                              {item.valueEn}
                            </MenuItem>
                          ))
                        ) : statusList.data instanceof Object ? (
                          <MenuItem
                            key={(statusList.data as any).id}
                            value={(statusList.data as any).id}
                          >
                            {(statusList.data as any).valueEn}
                          </MenuItem>
                        ) : null,

                        IconComponent: () => null,
                        renderValue: (value: any) => {
                          if (Array.isArray(statusList.data)) {
                            const selected = statusList.data.filter(
                              (item: any) => value.includes(item.id)
                            );
                            return selected
                              .map((item) => item.valueEn)
                              .join(", ");
                          } else if (statusList.data instanceof Object) {
                            return (statusList.data as any).valueEn || "";
                          }
                          return "";
                        },
                      }}
                      value={formData.statusId || StatusValues.Draft}
                      onChange={(e) =>
                        setFormData((prevData) => ({
                          ...prevData,
                          statusId: e.target.value as number,
                        }))
                      }
                      multiple={false}
                      required={true}
                      disabled={!offerIssuerInfo.isCmaConfirmed ? true : false}
                      error={touched.statusId && formData.statusId === null}
                      helperText={
                        touched.statusId && formData.statusId === null
                          ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                          : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <CustomTextField
                      label="Cancel Reason"
                      name="cancelReason"
                      type="text"
                      value={formData.cancelReason}
                      onChange={(e) =>
                        setFormData((prevData) => ({
                          ...prevData,
                          cancelReason: e.target.value,
                        }))
                      }
                      required={true}
                      disabled={formData.statusId !== StatusValues.Cancelled}
                      error={
                        touched.cancelReason &&
                        formData.cancelReason.trim() === ""
                      }
                      helperText={
                        touched.cancelReason &&
                        formData.cancelReason.trim() === ""
                          ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                          : ""
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomDatePicker
                    value={offerIssuerInfo.activationDate}
                    label="Date of activation on website "
                    required={offerIssuerInfo.isCmaConfirmed ? true : false}
                    disabled={!offerIssuerInfo.isCmaConfirmed ? true : false}
                    onChange={(date: string) => {
                      setOfferIssuerInfo({
                        ...offerIssuerInfo,
                        activationDate: date,
                      });
                    }}
                    error={
                      touched.activationDate &&
                      offerIssuerInfo.activationDate.trim() === ""
                    }
                    helperText={
                      touched.activationDate &&
                      offerIssuerInfo.activationDate.trim() === ""
                        ? `${VALIDATION_ACTIVATION_DATE_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    </form>
  );
};

export default OpportunityDetailsForm;
